/**
 * TransactionDialog Component
 * Handles Add/Edit Expense/Income and Transfer dialogs
 * Extracted from Activity.tsx to improve code organization
 */

import { ReactElement, FormEvent, useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Stack,
  IconButton,
  Menu,
  MenuItem,
  InputAdornment,
  Autocomplete,
  Chip,
  CircularProgress,
  Divider,
  Collapse,
  Paper,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import InfoIcon from "@mui/icons-material/Info";
import { Account, TransactionCategory, TransactionCategoryGroup, TransactionCategoryDetail, Currency, TimeFormat, DateFormat, TransactionCustomFieldValues } from "../../../types";
import { TransactionWithDate } from "../../../utils/transactionProcessing";
import { formatTimeLocal, getCurrentTime, formatCurrency, formatRate, convertTo12Hour, convertTo24Hour, getTransactionFriendlyId, copyToClipboard, formatDateForDisplay } from "../../../utils/transactionUtils";
import RefreshIconButton from "../../../components/RefreshIconButton";
import CopyFeedbackTooltip from "../../../components/CopyFeedbackTooltip";
import { validateAmountDecimalPlaces, formatAmountToCurrencyDecimalPlaces, convertFormattedAmountToRawValue, getCurrencyDecimalPlaces } from "../../../utils/currencyDefaults";
import { useTransactionCustomFields } from "../hooks/useTransactionCustomFields";
import { fieldAppliesToScope } from "../../../utils/transactionCustomFieldsDerivation";
import { AttachmentIcon } from "./AttachmentIcon";
import { TransactionFieldIcon } from "../../TransactionFieldsSettings/components/TransactionFieldIcon";
import type { CustomFieldAttachmentValue, TransactionCustomFieldScopeType } from "../../../types";
import { api } from "../../../services/api";

// Form data interfaces
interface FormData {
  title: string;
  category: string;
  amount: string;
  currency: string;
  rate: string;
  defaultCurrencyRate: string; // Rate from transaction currency to default currency
  date: string;
  time: string;
  accountId: string;
  accountGroup: string;
  details?: Record<string, any>;
  customFieldValues?: TransactionCustomFieldValues;
  customFieldPendingFiles?: Record<string, File>;
  feeAmount?: string;
  feeAccountId?: string;
}

interface TransferFormData {
  amount: string;
  date: string;
  time: string;
  fromAccountId: string;
  toAccountId: string;
  transferRate: string;
  customFieldValues?: TransactionCustomFieldValues;
  customFieldPendingFiles?: Record<string, File>;
  feeAmount?: string;
  feeAccountId?: string;
}

interface TransactionDialogProps {
  workspaceId: string;
  // Dialog state
  dialogOpen: boolean;
  transferDialogOpen: boolean;
  dialogType: "expense" | "income";
  editingTransaction: TransactionWithDate | null;
  isSubmitting: boolean;
  isSubmittingTransfer: boolean;
  
  // Form data
  formData: FormData;
  transferFormData: TransferFormData;
  
  // Additional state
  showTime: boolean;
  showTransferTime: boolean;
  currencyMenuAnchorEl: HTMLElement | null;
  uploadingAttachment: boolean;
  uploadingTransferAttachment: boolean;
  selectedCategoryDetails: TransactionCategoryDetail[];
  newlyAddedDetailOptions: Record<string, string[]>;
  detailInputValues: Record<string, string>;
  openDetailDropdowns: Set<string>;
  rateManuallyEdited: boolean;
  defaultCurrencyRateManuallyEdited: boolean;
  transferRateManuallyEdited: boolean;
  
  // Data
  accounts: Account[];
  currencies: Currency[];
  defaultCurrency: string;
  expenseCategories: TransactionCategory[];
  incomeCategories: TransactionCategory[];
  categoryGroups: TransactionCategoryGroup[];
  allowExpenseFee?: boolean;
  allowTransferFee?: boolean;

  // Handlers
  setDialogOpen: (open: boolean) => void;
  setTransferDialogOpen: (open: boolean) => void;
  setDialogType: (type: "expense" | "income") => void;
  setEditingTransaction: (transaction: TransactionWithDate | null) => void;
  setIsSubmitting: (submitting: boolean) => void;
  setIsSubmittingTransfer: (submitting: boolean) => void;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  setTransferFormData: React.Dispatch<React.SetStateAction<TransferFormData>>;
  setShowTime: (show: boolean) => void;
  setShowTransferTime: (show: boolean) => void;
  setShowTransferFee: (show: boolean) => void;
  setCurrencyMenuAnchorEl: (el: HTMLElement | null) => void;
  setUploadingAttachment: (uploading: boolean) => void;
  setUploadingTransferAttachment: (uploading: boolean) => void;
  setSelectedCategoryDetails: React.Dispatch<React.SetStateAction<TransactionCategoryDetail[]>>;
  setNewlyAddedDetailOptions: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
  setDetailInputValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setOpenDetailDropdowns: React.Dispatch<React.SetStateAction<Set<string>>>;
  setRateManuallyEdited: (edited: boolean) => void;
  setDefaultCurrencyRateManuallyEdited: (edited: boolean) => void;
  setTransferRateManuallyEdited: (edited: boolean) => void;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  handleTransferSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  handleDialogClose: () => void;
  handleTransferDialogClose: () => void;
  handleAccountChange: (accountId: string) => void;
  handleRefreshRate: () => void;
  handleRefreshDefaultCurrencyRate: () => void;
  handleRefreshTransferRate: () => void;
  handleCreateDetailOption: (detailName: string, inputValue: string) => void;
  deriveCategoryDetails: (categoryName: string, preserveExistingValues?: boolean) => void;
  calculateTransferRate: (fromCurrency: string, toCurrency: string) => number;
  getConvertedAmount: () => number | null;
  isTransactionFormValid: boolean;
  hasTransactionChanges: () => boolean;
  isTransferFormValid: boolean;
  hasTransferChanges: () => boolean;
  timeFormat?: TimeFormat; // "12h" or "24h", defaults to "24h"
  /** Workspace date format for the date field display (MM/DD/YYYY or DD/MM/YYYY). */
  dateFormat?: DateFormat;
  /** When true, show same form read-only with Close + Edit transaction buttons (e.g. shared URL). */
  viewOnly?: boolean;
  onSwitchToEdit?: () => void;
  /** Prefix for friendly transaction ID (e.g. "DV" for "DV-1"). */
  transactionIdPrefix?: string;
  /** Next sequence for new transactions (show "next ID" in create mode). */
  transactionIdNextSequence?: number;
  /** Called when secure attachment URL fails (e.g. not signed in). */
  onAttachmentError?: (message: string) => void;
}

const TransactionDialog = ({
  workspaceId,
  dialogOpen,
  transferDialogOpen,
  dialogType,
  editingTransaction,
  isSubmitting,
  isSubmittingTransfer,
  formData,
  transferFormData,
  showTime,
  showTransferTime,
  currencyMenuAnchorEl,
  uploadingAttachment,
  uploadingTransferAttachment,
  selectedCategoryDetails,
  newlyAddedDetailOptions,
  detailInputValues,
  openDetailDropdowns,
  rateManuallyEdited,
  defaultCurrencyRateManuallyEdited,
  transferRateManuallyEdited,
  accounts,
  currencies,
  defaultCurrency,
  expenseCategories,
  incomeCategories,
  categoryGroups,
  allowExpenseFee = false,
  allowTransferFee = false,
  setDialogOpen,
  setTransferDialogOpen,
  setDialogType,
  setEditingTransaction,
  setIsSubmitting,
  setIsSubmittingTransfer,
  setFormData,
  setTransferFormData,
  setShowTime,
  setShowTransferTime,
  setShowTransferFee,
  setCurrencyMenuAnchorEl,
  setUploadingAttachment,
  setUploadingTransferAttachment,
  setSelectedCategoryDetails,
  setNewlyAddedDetailOptions,
  setDetailInputValues,
  setOpenDetailDropdowns,
  setRateManuallyEdited,
  setDefaultCurrencyRateManuallyEdited,
  setTransferRateManuallyEdited,
  handleSubmit,
  handleTransferSubmit,
  handleDialogClose,
  handleTransferDialogClose,
  handleAccountChange,
  handleRefreshRate,
  handleRefreshDefaultCurrencyRate,
  handleRefreshTransferRate,
  handleCreateDetailOption,
  deriveCategoryDetails,
  calculateTransferRate,
  getConvertedAmount,
  isTransactionFormValid,
  hasTransactionChanges,
  isTransferFormValid,
  hasTransferChanges,
  timeFormat = "24h",
  dateFormat = "MM/DD/YYYY",
  viewOnly = false,
  onSwitchToEdit,
  transactionIdPrefix = "",
  transactionIdNextSequence,
  onAttachmentError,
}: TransactionDialogProps): ReactElement => {
  const currencyMenuOpen = Boolean(currencyMenuAnchorEl);
  const dateInputRef = useRef<HTMLInputElement>(null);
  const transferDateInputRef = useRef<HTMLInputElement>(null);
  const timeInputRef = useRef<HTMLInputElement>(null);
  const transferTimeInputRef = useRef<HTMLInputElement>(null);
  const customFieldFileInputRef = useRef<HTMLInputElement>(null);
  const transferCustomFieldFileInputRef = useRef<HTMLInputElement>(null);
  const customFieldFileTargetRef = useRef<string | null>(null);

  const { fields: expenseIncomeCustomFields } = useTransactionCustomFields({
    workspaceId,
    transactionType: dialogType,
  });
  const { fields: transferCustomFields } = useTransactionCustomFields({
    workspaceId,
    transactionType: "transfer",
  });

  // Ensure only fields for the current transaction type are shown (defensive filter for legacy data)
  const expenseIncomeFieldsForType = useMemo(
    () => expenseIncomeCustomFields.filter((f) => fieldAppliesToScope(f, dialogType as TransactionCustomFieldScopeType)),
    [expenseIncomeCustomFields, dialogType]
  );
  const transferFieldsForType = useMemo(
    () => transferCustomFields.filter((f) => fieldAppliesToScope(f, "transfer")),
    [transferCustomFields]
  );

  // Local state for 12-hour format text inputs (to allow free typing)
  const [timeInputValue, setTimeInputValue] = useState<string>("");
  const [transferTimeInputValue, setTransferTimeInputValue] = useState<string>("");
  const [lastCopiedKey, setLastCopiedKey] = useState<string | null>(null);
  const copyFeedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showCopiedFeedback = useCallback((key: string) => {
    if (copyFeedbackTimeoutRef.current) clearTimeout(copyFeedbackTimeoutRef.current);
    setLastCopiedKey(key);
    copyFeedbackTimeoutRef.current = setTimeout(() => {
      setLastCopiedKey(null);
      copyFeedbackTimeoutRef.current = null;
    }, 2000);
  }, []);
  useEffect(() => () => { if (copyFeedbackTimeoutRef.current) clearTimeout(copyFeedbackTimeoutRef.current); }, []);

  /** Open attachment in new tab. Open window synchronously on click so popup blockers allow it (desktop and mobile). */
  const openAttachment = useCallback(
    async (url: string) => {
      const newWindow = window.open("", "_blank");
      try {
        const { url: signedUrl } = await api.getAttachmentSignedUrl(workspaceId, url);
        if (newWindow) newWindow.location.href = signedUrl;
      } catch (e) {
        if (newWindow) newWindow.close();
        onAttachmentError?.(e instanceof Error ? e.message : "Could not open attachment");
      }
    },
    [workspaceId, onAttachmentError]
  );

  // Format time for display according to workspace setting
  // Note: HTML5 time input always uses 24-hour format internally
  // For 12-hour display, we'll show formatted text alongside or use a custom format
  const formatTimeForDisplay = useMemo(() => {
    return (time24: string): string => {
      if (!time24 || !time24.trim()) return "";
      if (timeFormat === "12h") {
        const { hour, minute, period } = convertTo12Hour(time24);
        return `${hour}:${minute} ${period}`;
      }
      return time24; // Already in 24-hour format
    };
  }, [timeFormat]);
  
  // Sync local input state with formData.time when formData.time changes externally (both 12h and 24h use display format)
  useEffect(() => {
    setTimeInputValue(formData.time ? formatTimeForDisplay(formData.time) : "");
  }, [formData.time, timeFormat, formatTimeForDisplay]);
  
  useEffect(() => {
    setTransferTimeInputValue(transferFormData.time ? formatTimeForDisplay(transferFormData.time) : "");
  }, [transferFormData.time, timeFormat, formatTimeForDisplay]);
  
  // Parse time from display format back to 24-hour format
  // This handles both 12h and 24h inputs
  const parseTimeInput = (inputValue: string): string => {
    if (!inputValue || !inputValue.trim()) return "";
    // If it's already in HH:mm format (from HTML5 time input), return as-is
    if (/^\d{1,2}:\d{2}$/.test(inputValue.trim())) {
      const parts = inputValue.trim().split(":");
      const hour = parseInt(parts[0], 10);
      const minute = parts[1];
      // If hour is > 12, it's already 24-hour format
      if (hour > 12) {
        return `${String(hour).padStart(2, "0")}:${minute}`;
      }
      // Otherwise, assume it's 24-hour format if it's 0-23
      if (hour >= 0 && hour <= 23) {
        return `${String(hour).padStart(2, "0")}:${minute}`;
      }
    }
    // If it's in 12-hour format (h:mm AM/PM or hh:mm AM/PM), convert it
    const match = inputValue.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (match) {
      const hour = parseInt(match[1], 10);
      const minute = match[2];
      const period = match[3].toUpperCase() as "AM" | "PM";
      if (hour >= 1 && hour <= 12 && minute.length === 2) {
        return convertTo24Hour(hour, minute, period);
      }
    }
    // Try to parse as just time without AM/PM (assume current format)
    const simpleMatch = inputValue.match(/(\d{1,2}):(\d{2})/);
    if (simpleMatch) {
      const hour = parseInt(simpleMatch[1], 10);
      const minute = simpleMatch[2];
      if (hour >= 0 && hour <= 23 && minute.length === 2) {
        return `${String(hour).padStart(2, "0")}:${minute}`;
      }
    }
    return ""; // Invalid format
  };
  
  return (
    <>
      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
        disableEnforceFocus={false}
        disableAutoFocus={false}
        PaperProps={{
          sx: { maxHeight: "90vh" },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
            <Stack direction="row" spacing={2} alignItems="baseline" sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6">
                {editingTransaction
                  ? (viewOnly ? "View" : "Edit") + ` ${dialogType === "income" ? "Income" : "Expense"}`
                  : `Add ${dialogType === "income" ? "Income" : "Expense"}`}
              </Typography>
              {(editingTransaction
                ? getTransactionFriendlyId(transactionIdPrefix, editingTransaction.friendlyIdSequence)
                : transactionIdPrefix && transactionIdNextSequence != null && getTransactionFriendlyId(transactionIdPrefix, transactionIdNextSequence)) && (
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "baseline",
                    ml: 1,
                    "& .copy-id-btn": { opacity: 0, transition: "opacity 0.15s ease" },
                    "&:hover .copy-id-btn, &:focus-within .copy-id-btn": { opacity: 1 },
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    ID: {editingTransaction
                      ? getTransactionFriendlyId(transactionIdPrefix, editingTransaction.friendlyIdSequence)
                      : getTransactionFriendlyId(transactionIdPrefix, transactionIdNextSequence)}
                  </Typography>
                  <CopyFeedbackTooltip copyLabel="Copy ID" feedbackKey={editingTransaction ? `dialog-id-${editingTransaction.id}` : "dialog-id-new"} lastCopiedKey={lastCopiedKey} isMobile={false}>
                    <IconButton
                      className="copy-id-btn"
                      size="small"
                      onClick={() => {
                        const id = editingTransaction
                          ? getTransactionFriendlyId(transactionIdPrefix, editingTransaction.friendlyIdSequence)
                          : getTransactionFriendlyId(transactionIdPrefix, transactionIdNextSequence);
                        if (id) copyToClipboard(id).then((ok) => ok && showCopiedFeedback(editingTransaction ? `dialog-id-${editingTransaction.id}` : "dialog-id-new"));
                      }}
                      sx={{ p: 0.25, ml: 0.25 }}
                      aria-label="Copy transaction ID"
                    >
                      <ContentCopyIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </CopyFeedbackTooltip>
                </Box>
              )}
            </Stack>
            <IconButton size="small" onClick={handleDialogClose} sx={{ flexShrink: 0 }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit} id="transaction-form">
            <Stack component="fieldset" spacing={2} sx={{ border: "none", padding: 0, margin: 0, marginTop: "10px", minWidth: 0 }} disabled={viewOnly}>
              {/* Date and Time - Time shown inline in date field. Display uses workspace date format (MM/DD or DD/MM). */}
              <input
                type="date"
                ref={dateInputRef}
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                aria-hidden
                tabIndex={-1}
                style={{ position: "absolute", opacity: 0, pointerEvents: "none", width: 0, height: 0 }}
              />
              <TextField
                required
                label="Date"
                type="text"
                value={formatDateForDisplay(formData.date, dateFormat)}
                onClick={() => dateInputRef.current?.showPicker?.()}
                inputProps={{
                  style: { cursor: "pointer" },
                  readOnly: true,
                }}
                sx={{ width: "100%" }}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          dateInputRef.current?.showPicker?.();
                        }}
                        sx={{ 
                          color: "text.secondary",
                          "&:hover": { bgcolor: "action.hover" },
                        }}
                      >
                        <CalendarTodayIcon sx={{ fontSize: "1.25rem" }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      {!showTime ? (
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            const now = getCurrentTime();
                            setShowTime(true);
                            setFormData({ ...formData, time: now });
                            if (timeFormat === "12h") {
                              setTimeInputValue(now ? formatTimeForDisplay(now) : "");
                            }
                          }}
                          sx={{
                            minWidth: "auto",
                            px: 1,
                            py: 0.5,
                            color: "text.primary",
                            fontWeight: "normal",
                            textTransform: "none",
                            "&:hover": {
                              bgcolor: "action.hover",
                            },
                          }}
                        >
                          Time
                        </Button>
                      ) : (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* Time field: 12h shows "h:mm AM/PM", 24h shows "HH:mm" per workspace setting */}
                          <TextField
                            inputRef={timeInputRef}
                            type="text"
                            value={timeInputValue}
                            onChange={(e) => {
                              const inputValue = e.target.value;
                              setTimeInputValue(inputValue);
                              const newTime24 = parseTimeInput(inputValue.trim());
                              if (newTime24) {
                                setFormData({ ...formData, time: newTime24 });
                              } else if (!inputValue.trim()) {
                                setFormData({ ...formData, time: "" });
                              }
                            }}
                            onBlur={(e) => {
                              const inputValue = e.target.value.trim();
                              if (inputValue) {
                                const newTime24 = parseTimeInput(inputValue);
                                if (newTime24) {
                                  setFormData({ ...formData, time: newTime24 });
                                  setTimeInputValue(formatTimeForDisplay(newTime24));
                                } else {
                                  setFormData({ ...formData, time: "" });
                                  setTimeInputValue("");
                                }
                              } else {
                                setFormData({ ...formData, time: "" });
                                setTimeInputValue("");
                              }
                            }}
                            placeholder={timeFormat === "12h" ? "h:mm AM/PM" : "HH:mm"}
                            size="small"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            sx={{ 
                              width: timeFormat === "12h" ? 120 : 100,
                              "& .MuiOutlinedInput-root": {
                                height: 32,
                                paddingLeft: 0,
                                paddingRight: 0,
                                "& fieldset": {
                                  borderColor: "rgba(0, 0, 0, 0.23) !important",
                                },
                              },
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              setFormData({ ...formData, time: "" });
                              setShowTime(false);
                            }}
                            sx={{
                              color: "text.secondary",
                              "&:hover": {
                                color: "text.primary",
                                bgcolor: "action.hover",
                              },
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </InputAdornment>
                  ),
                }}
              />

              {/* Amount with Currency */}
              {(() => {
                // Get current currency - always use defaultCurrency if not set, never auto-select account currency
                const currentCurrency = formData.currency || defaultCurrency;
                
                // Validate decimal places
                const validation = validateAmountDecimalPlaces(
                  formData.amount,
                  currentCurrency,
                  currencies,
                  defaultCurrency
                );
                
                // Calculate converted amount for helperText when currencies differ
                const calculationHelperText = (() => {
                  if (!formData.accountId || !formData.currency || !formData.amount) return null;
                  
                  const account = accounts.find((acc) => acc.id === formData.accountId);
                  const accountCurrency = account?.currency || defaultCurrency;
                  const transactionCurrency = formData.currency || defaultCurrency;
                  const currenciesDiffer = transactionCurrency !== accountCurrency;
                  
                  if (!currenciesDiffer) return null;
                  
                  const transactionAmount = parseFloat(formData.amount);
                  if (isNaN(transactionAmount) || transactionAmount <= 0) return null;
                  
                  // Get the rate to use: stored rate if valid, otherwise calculate it
                  const storedRate = formData.rate ? parseFloat(formData.rate) : null;
                  const calculatedRate = calculateTransferRate(transactionCurrency, accountCurrency);
                  
                  // Use stored rate if it's valid and reasonable, otherwise use calculated rate
                  // Validate rate: should be between 0.0001 and 10000 (covers most currency conversions)
                  const isValidStoredRate = storedRate !== null && !isNaN(storedRate) && storedRate > 0 && storedRate >= 0.0001 && storedRate <= 10000;
                  const rateToUse = isValidStoredRate ? storedRate : calculatedRate;
                  
                  // Final validation: ensure calculated rate is also reasonable
                  if (!rateToUse || isNaN(rateToUse) || rateToUse <= 0 || rateToUse < 0.0001 || rateToUse > 10000) {
                    return null;
                  }
                  
                  const convertedAmount = transactionAmount * rateToUse;
                  
                  if (isNaN(convertedAmount) || convertedAmount <= 0) return null;
                  
                  const accountDecimalPlaces = getCurrencyDecimalPlaces(accountCurrency, currencies, defaultCurrency);
                  const transactionDecimalPlaces = getCurrencyDecimalPlaces(transactionCurrency, currencies, defaultCurrency);
                  const formattedAmount = formatCurrency(transactionAmount, transactionCurrency, transactionDecimalPlaces);
                  const formattedConverted = formatCurrency(convertedAmount, accountCurrency, accountDecimalPlaces);
                  
                  return `Calculation: ${formattedAmount} = ${formattedConverted}`;
                })();
                
                // Combine validation error and calculation helper text
                const helperText = validation.errorMessage 
                  ? (calculationHelperText ? `${validation.errorMessage} | ${calculationHelperText}` : validation.errorMessage)
                  : calculationHelperText || undefined;
                
                return (
                  <TextField
                    required
                    fullWidth
                    label="Amount"
                    type="number"
                    inputProps={{ step: "any", min: "0" }}
                    value={formData.amount}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Only allow digits and a single decimal point
                      // Reject scientific notation, negative signs, and other invalid characters
                      if (value === "" || /^\d*\.?\d*$/.test(value)) {
                        setFormData({ ...formData, amount: value });
                      }
                    }}
                    onPaste={(e) => {
                      e.preventDefault();
                      const pastedText = e.clipboardData.getData('text');
                      // Sanitize pasted content: remove all non-digit and non-decimal characters
                      // Also remove scientific notation, negative signs, etc.
                      const sanitized = pastedText.replace(/[^\d.]/g, '');
                      // Remove multiple decimal points, keeping only the first one
                      const parts = sanitized.split('.');
                      const cleaned = parts.length > 0 
                        ? parts[0] + (parts.length > 1 ? '.' + parts.slice(1).join('') : '')
                        : '';
                      // Only allow valid number format
                      if (cleaned === "" || /^\d*\.?\d*$/.test(cleaned)) {
                        setFormData({ ...formData, amount: cleaned });
                      }
                    }}
                    onFocus={(e) => {
                      // Convert formatted amount back to raw value for editing
                      const value = e.target.value;
                      if (value && value.trim() !== "") {
                        const rawValue = convertFormattedAmountToRawValue(value);
                        if (rawValue !== value) {
                          setFormData({ ...formData, amount: rawValue });
                        }
                      }
                    }}
                    onBlur={(e) => {
                      // Format amount to match currency decimal places when field loses focus
                      const value = e.target.value;
                      if (value && value.trim() !== "") {
                        const formatted = formatAmountToCurrencyDecimalPlaces(
                          value,
                          currentCurrency,
                          currencies,
                          defaultCurrency
                        );
                        setFormData({ ...formData, amount: formatted });
                      }
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                        e.preventDefault();
                      }
                    }}
                    error={!validation.isValid}
                    helperText={helperText}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              setCurrencyMenuAnchorEl(e.currentTarget);
                            }}
                            sx={{
                              minWidth: "auto",
                              px: 1,
                              py: 0.5,
                              color: "text.primary",
                              fontWeight: "normal",
                              textTransform: "none",
                              "&:hover": {
                                bgcolor: "action.hover",
                              },
                            }}
                          >
                            {currentCurrency}
                          </Button>
                        </InputAdornment>
                      ),
                      sx: {
                        "& input[type=number]": {
                          MozAppearance: "textfield",
                        },
                        "& input[type=number]::-webkit-outer-spin-button": {
                          WebkitAppearance: "none",
                          margin: 0,
                        },
                        "& input[type=number]::-webkit-inner-spin-button": {
                          WebkitAppearance: "none",
                          margin: 0,
                        },
                      },
                    }}
                    placeholder="0.00"
                  />
                );
              })()}
              
              {/* Currency Menu */}
              <Menu
                anchorEl={currencyMenuAnchorEl}
                open={currencyMenuOpen}
                onClose={() => setCurrencyMenuAnchorEl(null)}
                PaperProps={{
                  sx: {
                    maxHeight: 300,
                    width: "200px",
                  },
                }}
              >
                <MenuItem
                  onClick={() => {
                    const account = formData.accountId ? accounts.find((acc) => acc.id === formData.accountId) : null;
                    const accountCurrency = account?.currency || defaultCurrency;
                    const selectedCurrency = defaultCurrency;
                    
                    let rate = "";
                    if (selectedCurrency !== accountCurrency) {
                      const calculatedRate = calculateTransferRate(selectedCurrency, accountCurrency);
                      rate = formatRate(calculatedRate);
                    }
                    
                    setFormData({
                      ...formData,
                      currency: selectedCurrency,
                      rate: rate,
                      defaultCurrencyRate: "", // Same as default currency
                    });
                    setRateManuallyEdited(false);
                    setDefaultCurrencyRateManuallyEdited(false);
                    setCurrencyMenuAnchorEl(null);
                  }}
                  selected={Boolean(formData.currency === defaultCurrency || (!formData.currency && !formData.accountId) || (formData.accountId && !formData.currency && accounts.find((acc) => acc.id === formData.accountId)?.currency === defaultCurrency))}
                >
                  {defaultCurrency}
                </MenuItem>
                <Divider />
                {currencies
                  .filter((c) => c.code && c.code !== defaultCurrency)
                  .map((currency) => {
                    const currencyCode = currency.code || "";
                    
                    return (
                      <MenuItem
                        key={currencyCode}
                        onClick={() => {
                          const account = formData.accountId ? accounts.find((acc) => acc.id === formData.accountId) : null;
                          const accountCurrency = account?.currency || defaultCurrency;
                          const selectedCurrency = currencyCode;
                          
                          let rate = "";
                          if (selectedCurrency !== accountCurrency) {
                            const calculatedRate = calculateTransferRate(selectedCurrency, accountCurrency);
                            rate = formatRate(calculatedRate);
                          }
                          
                          let defaultCurrencyRate = "";
                          if (selectedCurrency !== defaultCurrency && accountCurrency !== defaultCurrency) {
                            // Calculate rate from account currency to default currency (not expense currency)
                            const calculatedAccountToDefaultRate = calculateTransferRate(accountCurrency, defaultCurrency);
                            defaultCurrencyRate = formatRate(calculatedAccountToDefaultRate);
                          }
                          
                          setFormData({
                            ...formData,
                            currency: selectedCurrency,
                            rate: rate,
                            defaultCurrencyRate: defaultCurrencyRate,
                          });
                          setRateManuallyEdited(false);
                          setDefaultCurrencyRateManuallyEdited(false);
                          setCurrencyMenuAnchorEl(null);
                        }}
                        selected={formData.currency === currencyCode}
                      >
                        {currencyCode}
                      </MenuItem>
                    );
                  })}
              </Menu>

              {/* Account */}
              <Autocomplete
                fullWidth
                options={accounts
                  .filter((acc) => !acc.isArchived)
                  .sort((a, b) => {
                    if (a.group !== b.group) {
                      return a.group.localeCompare(b.group);
                    }
                    return a.accountName.localeCompare(b.accountName);
                  })}
                value={accounts.find((acc) => acc.id === formData.accountId && !acc.isArchived) || null}
                onChange={(_, newValue: Account | null) => {
                  if (newValue) {
                    handleAccountChange(newValue.id);
                  } else {
                    setFormData({
                      ...formData,
                      accountId: "",
                      accountGroup: "",
                      currency: "",
                      rate: "",
                    });
                  }
                }}
                groupBy={(option: Account) => option.group}
                getOptionLabel={(option: Account) => `${option.accountName} (${formatCurrency(option.amount || 0, option.currency, getCurrencyDecimalPlaces(option.currency, currencies, defaultCurrency))} ${option.currency})`}
                filterOptions={(options: Account[], { inputValue }) => {
                  const searchValue = inputValue.toLowerCase();
                  return options.filter(
                    (option) =>
                      option.accountName.toLowerCase().includes(searchValue) ||
                      option.group.toLowerCase().includes(searchValue)
                  );
                }}
                ListboxProps={{
                  onWheel: (e: React.WheelEvent) => {
                    const listbox = e.currentTarget;
                    if (listbox.scrollHeight <= listbox.clientHeight) return;
                    e.stopPropagation();
                    e.preventDefault();
                    listbox.scrollTop += e.deltaY;
                  },
                }}
                renderOption={(props, option: Account) => (
                  <Box component="li" {...props} key={option.id} sx={{ pl: 3 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                      <Typography variant="body2">
                        {option.accountName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                        {formatCurrency(option.amount || 0, option.currency, getCurrencyDecimalPlaces(option.currency, currencies, defaultCurrency))} ({option.currency})
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderGroup={(params) => (
                  <li key={params.key}>
                    <Box sx={{ position: "sticky", top: 0, bgcolor: "background.paper", zIndex: 1, py: 0.5, px: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                        {params.group}
                      </Typography>
                    </Box>
                    <Box component="ul" sx={{ p: 0 }}>
                      {params.children}
                    </Box>
                  </li>
                )}
                renderInput={(params) => {
                  const account = formData.accountId ? accounts.find((acc) => acc.id === formData.accountId && !acc.isArchived) : null;
                  const accountCurrency = account?.currency || defaultCurrency;
                  const transactionCurrency = formData.currency || accountCurrency;
                  const balanceHelperText = (dialogType === "expense" || dialogType === "income") && account && formData.amount
                    ? (() => {
                        const amount = parseFloat(formData.amount);
                        if (!isNaN(amount)) {
                          let amountInAccountCurrency = amount;
                          if (transactionCurrency !== accountCurrency && formData.rate) {
                            const rate = parseFloat(formData.rate);
                            if (!isNaN(rate) && rate > 0) {
                              amountInAccountCurrency = amount * rate;
                            }
                          }
                          
                          // Calculate account balance before transaction (if editing)
                          let balanceBeforeTransaction = account.amount;
                          if (editingTransaction && editingTransaction.accountId === account.id) {
                            // Reverse the old transaction's effect
                            const oldAmount = editingTransaction.amount || 0;
                            let oldAmountInAccountCurrency = oldAmount;
                            
                            // Handle currency conversion for old transaction
                            if (editingTransaction.currency && editingTransaction.currency !== accountCurrency && editingTransaction.rate) {
                              const oldRate = parseFloat(String(editingTransaction.rate));
                              if (!isNaN(oldRate) && oldRate > 0) {
                                oldAmountInAccountCurrency = oldAmount * oldRate;
                              }
                            }
                            
                            // Reverse: if it was income, subtract; if expense, add back
                            if (editingTransaction.isIncome || editingTransaction.type === "income") {
                              balanceBeforeTransaction = account.amount - oldAmountInAccountCurrency;
                            } else {
                              balanceBeforeTransaction = account.amount + oldAmountInAccountCurrency;
                            }
                          }
                          
                          // Apply new transaction amount to balance before transaction
                          const newBalance = dialogType === "expense" 
                            ? balanceBeforeTransaction - amountInAccountCurrency
                            : balanceBeforeTransaction + amountInAccountCurrency;
                          const isNegative = newBalance < 0;
                          const transactionType = dialogType === "expense" ? "expense" : "income";
                          return `Balance after ${transactionType}: ${formatCurrency(newBalance, accountCurrency)}${isNegative ? " (negative)" : ""}`;
                        }
                        return undefined;
                      })()
                    : undefined;
                  const balanceHelperColor = (dialogType === "expense" || dialogType === "income") && account && formData.amount
                    ? (() => {
                        const amount = parseFloat(formData.amount);
                        if (!isNaN(amount)) {
                          let amountInAccountCurrency = amount;
                          if (transactionCurrency !== accountCurrency && formData.rate) {
                            const rate = parseFloat(formData.rate);
                            if (!isNaN(rate) && rate > 0) {
                              amountInAccountCurrency = amount * rate;
                            }
                          }
                          
                          // Calculate account balance before transaction (if editing)
                          let balanceBeforeTransaction = account.amount;
                          if (editingTransaction && editingTransaction.accountId === account.id) {
                            // Reverse the old transaction's effect
                            const oldAmount = editingTransaction.amount || 0;
                            let oldAmountInAccountCurrency = oldAmount;
                            
                            // Handle currency conversion for old transaction
                            if (editingTransaction.currency && editingTransaction.currency !== accountCurrency && editingTransaction.rate) {
                              const oldRate = parseFloat(String(editingTransaction.rate));
                              if (!isNaN(oldRate) && oldRate > 0) {
                                oldAmountInAccountCurrency = oldAmount * oldRate;
                              }
                            }
                            
                            // Reverse: if it was income, subtract; if expense, add back
                            if (editingTransaction.isIncome || editingTransaction.type === "income") {
                              balanceBeforeTransaction = account.amount - oldAmountInAccountCurrency;
                            } else {
                              balanceBeforeTransaction = account.amount + oldAmountInAccountCurrency;
                            }
                          }
                          
                          // Apply new transaction amount to balance before transaction
                          const newBalance = dialogType === "expense" 
                            ? balanceBeforeTransaction - amountInAccountCurrency
                            : balanceBeforeTransaction + amountInAccountCurrency;
                          return newBalance < 0 ? "warning.main" : "text.secondary";
                        }
                        return "text.secondary";
                      })()
                    : "text.secondary";
                  
                  return (
                    <TextField
                      {...params}
                      label="Account"
                      required
                      placeholder="Search by account name or group..."
                      helperText={balanceHelperText}
                      InputLabelProps={{ shrink: true }}
                      FormHelperTextProps={{
                        sx: { color: balanceHelperColor },
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderWidth: "1px",
                          },
                          "&:hover fieldset": {
                            borderWidth: "1px",
                          },
                          "&.Mui-focused fieldset": {
                            borderWidth: "1px",
                          },
                        },
                      }}
                    />
                  );
                }}
                isOptionEqualToValue={(option: Account, value: Account) => option.id === value.id}
              />

              {/* Expense Fee - right after Account when workspace allows */}
              {dialogType === "expense" && allowExpenseFee && (
                <>
                  <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                    <Button
                      type="button"
                      size="small"
                      variant={formData.feeAmount !== undefined ? "outlined" : "text"}
                      onClick={() => {
                        if (formData.feeAmount === undefined) {
                          setFormData({ ...formData, feeAmount: "", feeAccountId: "" });
                        } else {
                          setFormData({ ...formData, feeAmount: undefined, feeAccountId: undefined });
                        }
                      }}
                      sx={{
                        minWidth: "auto",
                        px: 1,
                        py: 0.25,
                        textTransform: "none",
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      {formData.feeAmount === undefined ? "+ Add fee" : "Remove fee"}
                    </Button>
                    <Tooltip title="Optional fee (e.g. bank fee):" placement="top">
                      <IconButton size="small" sx={{ p: 0.25, color: "text.disabled", "&:hover": { color: "text.secondary" } }} aria-label="Optional fee info">
                        <InfoIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {formData.feeAmount !== undefined && (() => {
                    const feeAccount = formData.feeAccountId ? accounts.find((acc) => acc.id === formData.feeAccountId && !acc.isArchived) : null;
                    const feeCurrency = feeAccount?.currency || defaultCurrency;
                    const feeAmountValidation = validateAmountDecimalPlaces(
                      formData.feeAmount ?? "",
                      feeCurrency,
                      currencies,
                      defaultCurrency
                    );
                    return (
                    <Stack direction="row" spacing={2}>
                      <TextField
                        label="Fee Amount"
                        type="number"
                        inputProps={{ step: "any", min: "0" }}
                        value={formData.feeAmount || ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "" || /^\d*\.?\d*$/.test(value)) {
                            setFormData({ ...formData, feeAmount: value });
                          }
                        }}
                        onPaste={(e) => {
                          e.preventDefault();
                          const pastedText = e.clipboardData.getData("text");
                          const sanitized = pastedText.replace(/[^\d.]/g, "");
                          const parts = sanitized.split(".");
                          const cleaned = parts.length > 0 ? parts[0] + (parts.length > 1 ? "." + parts.slice(1).join("") : "") : "";
                          if (cleaned === "" || /^\d*\.?\d*$/.test(cleaned)) {
                            setFormData({ ...formData, feeAmount: cleaned });
                          }
                        }}
                        onFocus={(e) => {
                          const value = e.target.value;
                          if (value && value.trim() !== "") {
                            const rawValue = convertFormattedAmountToRawValue(value);
                            if (rawValue !== value) {
                              setFormData({ ...formData, feeAmount: rawValue });
                            }
                          }
                        }}
                        onBlur={(e) => {
                          // Only format to currency decimal places when user has selected a fee account
                          if (!formData.feeAccountId) return;
                          const value = e.target.value;
                          if (value && value.trim() !== "") {
                            const formatted = formatAmountToCurrencyDecimalPlaces(value, feeCurrency, currencies, defaultCurrency);
                            setFormData({ ...formData, feeAmount: formatted });
                          }
                        }}
                        onKeyPress={(e) => {
                          if (e.key === "e" || e.key === "E" || e.key === "+" || e.key === "-") e.preventDefault();
                        }}
                        error={formData.feeAccountId ? !feeAmountValidation.isValid : false}
                        helperText={formData.feeAccountId ? (feeAmountValidation.errorMessage || undefined) : undefined}
                        sx={{ flex: 1 }}
                        placeholder="0.00"
                        InputProps={{
                          endAdornment: formData.feeAccountId ? (
                            <InputAdornment position="end">
                              <Typography variant="body2" color="text.secondary">
                                {feeAccount?.currency || ""}
                              </Typography>
                            </InputAdornment>
                          ) : undefined,
                        }}
                      />
                      <Autocomplete
                        sx={{ flex: 1 }}
                        options={accounts.filter((acc) => !acc.isArchived).sort((a, b) => (a.group !== b.group ? a.group.localeCompare(b.group) : a.accountName.localeCompare(b.accountName)))}
                        value={formData.feeAccountId ? accounts.find((acc) => acc.id === formData.feeAccountId && !acc.isArchived) || null : null}
                        onChange={(_, newValue: Account | null) => {
                          setFormData({ ...formData, feeAccountId: newValue?.id || "" });
                        }}
                        groupBy={(option: Account) => option.group}
                        getOptionLabel={(option: Account) =>
                          `${option.accountName} (${formatCurrency(option.amount || 0, option.currency, getCurrencyDecimalPlaces(option.currency, currencies, defaultCurrency))} ${option.currency})`
                        }
                        ListboxProps={{
                          onWheel: (e: React.WheelEvent) => {
                            const listbox = e.currentTarget;
                            if (listbox.scrollHeight <= listbox.clientHeight) return;
                            e.stopPropagation();
                            e.preventDefault();
                            listbox.scrollTop += e.deltaY;
                          },
                        }}
                        renderInput={(params) => <TextField {...params} label="Fee Account" />}
                      />
                    </Stack>
                    );
                  })()}
                </>
              )}

              {/* Exchange Rate Field - Show only when currencies differ */}
              {formData.accountId &&
                formData.currency &&
                accounts.find((acc) => acc.id === formData.accountId)?.currency !== formData.currency && (() => {
                  const account = accounts.find((acc) => acc.id === formData.accountId);
                  const accountCurrency = account?.currency || defaultCurrency;
                  const calculatedRate = calculateTransferRate(formData.currency, accountCurrency);
                  const currentRate = formData.rate ? parseFloat(formData.rate) : null;
                  // Use rounded values for comparison to avoid floating point precision issues
                  const roundedCalculatedRate = calculatedRate !== null ? Math.round(calculatedRate * 100000) / 100000 : null;
                  const roundedCurrentRate = currentRate !== null && !isNaN(currentRate) ? Math.round(currentRate * 100000) / 100000 : null;
                  const rateMatchesSetting = roundedCalculatedRate !== null && roundedCurrentRate !== null && Math.abs(roundedCurrentRate - roundedCalculatedRate) < 0.000001;
                  // Disable refresh button if rate matches setting (auto-filled or manually set to match) OR if currencies match
                  const shouldDisableRefresh = rateMatchesSetting || formData.currency === accountCurrency;
                  
                  return (
                    <TextField
                      required
                      fullWidth
                      label="Exchange Rate"
                      type="number"
                      inputProps={{ step: "0.000001", min: "0.000001" }}
                      value={formData.rate}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow digits and decimal point, reject scientific notation and negative signs
                        if (value === "" || /^\d*\.?\d*$/.test(value)) {
                          setFormData({ ...formData, rate: value });
                          setRateManuallyEdited(true);
                        }
                      }}
                      onPaste={(e) => {
                        e.preventDefault();
                        const pastedText = e.clipboardData.getData('text');
                        // Sanitize pasted content: remove all non-digit and non-decimal characters
                        const sanitized = pastedText.replace(/[^\d.]/g, '');
                        // Remove multiple decimal points, keeping only the first one
                        const parts = sanitized.split('.');
                        const cleaned = parts.length > 0 
                          ? parts[0] + (parts.length > 1 ? '.' + parts.slice(1).join('') : '')
                          : '';
                        // Only allow valid number format
                        if (cleaned === "" || /^\d*\.?\d*$/.test(cleaned)) {
                          setFormData({ ...formData, rate: cleaned });
                          setRateManuallyEdited(true);
                        }
                      }}
                      placeholder="Auto-filled from currency settings"
                      helperText={(() => {
                        // Exchange Rate: "1 ExpenseCurrency = X AccountCurrency | = Result"
                        // The rate stored is from expense currency to account currency
                        const currentRate = formData.rate ? parseFloat(formData.rate) : null;
                        const expenseToAccountRate = currentRate !== null && !isNaN(currentRate) && currentRate > 0
                          ? currentRate
                          : (formData.currency !== accountCurrency 
                            ? calculateTransferRate(formData.currency, accountCurrency)
                            : 1);
                        const expenseToAccountRateDisplay = formData.currency !== accountCurrency 
                          ? formatRate(expenseToAccountRate)
                          : "1";
                        
                        // Calculate converted amount (result in account currency)
                        const transactionAmount = formData.amount ? parseFloat(formData.amount) : null;
                        const convertedAmount = transactionAmount && expenseToAccountRate && !isNaN(expenseToAccountRate) && expenseToAccountRate > 0
                          ? transactionAmount * expenseToAccountRate
                          : null;
                        const accountDecimalPlaces = getCurrencyDecimalPlaces(accountCurrency, currencies, defaultCurrency);
                        const convertedAmountDisplay = convertedAmount 
                          ? formatCurrency(convertedAmount, accountCurrency, accountDecimalPlaces)
                          : "";
                        
                        // Build helper text: "1 AMD = 0.0027 EUR | = €8.46"
                        const rateText = `1 ${formData.currency} = ${expenseToAccountRateDisplay} ${accountCurrency}`;
                        const resultText = convertedAmountDisplay ? ` | = ${convertedAmountDisplay}` : "";
                        return rateText + resultText;
                      })()}
                      InputProps={{
                        endAdornment: (
                          <RefreshIconButton
                            onClick={handleRefreshRate}
                            disabled={shouldDisableRefresh}
                            disabledTooltip={rateMatchesSetting ? "Rate matches the setting rate." : "Click to update rate to current currency settings"}
                            tooltip="Refresh rate from currency settings"
                          />
                        ),
                        sx: {
                          "& input[type=number]": {
                            MozAppearance: "textfield",
                          },
                          "& input[type=number]::-webkit-outer-spin-button": {
                            WebkitAppearance: "none",
                            margin: 0,
                          },
                          "& input[type=number]::-webkit-inner-spin-button": {
                            WebkitAppearance: "none",
                            margin: 0,
                          },
                        },
                      }}
                    />
                  );
                })()}

              {/* Default Currency Rate Field - Show when transaction currency differs from default AND account currency differs from default (no need for 1 USD = 1 USD) */}
              {formData.accountId && formData.currency && formData.currency !== defaultCurrency && (() => {
                const account = accounts.find((acc) => acc.id === formData.accountId);
                const accountCurrency = account?.currency || defaultCurrency;
                if (accountCurrency === defaultCurrency) return null;
                // Calculate rate from account currency to default currency (for field value and comparison)
                const calculatedAccountToDefaultRate = calculateTransferRate(accountCurrency, defaultCurrency);
                const currentDefaultRate = formData.defaultCurrencyRate ? parseFloat(formData.defaultCurrencyRate) : null;
                // Use rounded values for comparison to avoid floating point precision issues
                const roundedCalculatedRate = calculatedAccountToDefaultRate !== null ? Math.round(calculatedAccountToDefaultRate * 100000) / 100000 : null;
                const roundedCurrentRate = currentDefaultRate !== null && !isNaN(currentDefaultRate) ? Math.round(currentDefaultRate * 100000) / 100000 : null;
                const defaultRateMatchesSetting = roundedCalculatedRate !== null && roundedCurrentRate !== null && Math.abs(roundedCurrentRate - roundedCalculatedRate) < 0.000001;
                // Disable refresh when rate matches setting (so button is not kept enabled when edit has same rate as settings)
                const shouldDisableDefaultRefresh = defaultRateMatchesSetting || formData.currency === defaultCurrency;
                
                // Calculate amount in default currency for display
                // Field stores account-to-default rate, but we need to convert expense amount to default currency
                // So: expenseAmount -> accountAmount (via exchange rate) -> defaultAmount (via account-to-default rate)
                const transactionAmount = formData.amount ? parseFloat(formData.amount) : null;
                const exchangeRate = formData.rate ? parseFloat(formData.rate) : null;
                const accountToDefaultRate = currentDefaultRate !== null && !isNaN(currentDefaultRate) ? currentDefaultRate : calculatedAccountToDefaultRate;
                
                // First convert expense amount to account currency, then to default currency
                let amountInDefaultCurrency: number | null = null;
                if (transactionAmount && exchangeRate && !isNaN(exchangeRate) && exchangeRate > 0) {
                  const amountInAccountCurrency = transactionAmount * exchangeRate;
                  if (accountToDefaultRate && !isNaN(accountToDefaultRate) && accountToDefaultRate > 0) {
                    amountInDefaultCurrency = amountInAccountCurrency * accountToDefaultRate;
                  }
                }
                const defaultDecimalPlaces = getCurrencyDecimalPlaces(defaultCurrency, currencies, defaultCurrency);
                const transactionDecimalPlaces = getCurrencyDecimalPlaces(formData.currency, currencies, defaultCurrency);
                
                return (
                  <TextField
                    required
                    fullWidth
                    label="Default Currency Rate"
                    type="number"
                    inputProps={{ step: "0.000001", min: "0.000001" }}
                    value={formData.defaultCurrencyRate}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow digits and decimal point, reject scientific notation and negative signs
                      if (value === "" || /^\d*\.?\d*$/.test(value)) {
                        setFormData({ ...formData, defaultCurrencyRate: value });
                        setDefaultCurrencyRateManuallyEdited(true);
                      }
                    }}
                    onPaste={(e) => {
                      e.preventDefault();
                      const pastedText = e.clipboardData.getData('text');
                      // Sanitize pasted content: remove all non-digit and non-decimal characters
                      const sanitized = pastedText.replace(/[^\d.]/g, '');
                      // Remove multiple decimal points, keeping only the first one
                      const parts = sanitized.split('.');
                      const cleaned = parts.length > 0 
                        ? parts[0] + (parts.length > 1 ? '.' + parts.slice(1).join('') : '')
                        : '';
                      // Only allow valid number format
                      if (cleaned === "" || /^\d*\.?\d*$/.test(cleaned)) {
                        setFormData({ ...formData, defaultCurrencyRate: cleaned });
                        setDefaultCurrencyRateManuallyEdited(true);
                      }
                    }}
                    placeholder="Auto-filled from currency settings"
                    helperText={(() => {
                      // Default Currency Rate: "1 AccountCurrency = DefaultCurrency | = Result"
                      // Use formData.defaultCurrencyRate if available, otherwise calculate from settings
                      let accountToDefaultRate = 1;
                      if (accountCurrency !== defaultCurrency) {
                        // Use stored rate from formData if available
                        if (currentDefaultRate !== null && !isNaN(currentDefaultRate) && currentDefaultRate > 0) {
                          accountToDefaultRate = currentDefaultRate;
                        } else {
                          // Fallback to calculated rate from settings
                          const accountCurrencyObj = currencies.find((c) => c.code === accountCurrency);
                          const accountRate = accountCurrencyObj?.rate;
                          if (accountRate !== null && accountRate !== undefined && accountRate > 0) {
                            // Rate from settings: 1 defaultCurrency = accountRate accountCurrency
                            // So: 1 accountCurrency = 1/accountRate defaultCurrency
                            accountToDefaultRate = 1 / accountRate;
                          } else {
                            // Fallback to calculated rate if not in settings
                            accountToDefaultRate = calculateTransferRate(accountCurrency, defaultCurrency);
                          }
                        }
                      }
                      // Show rate with more decimal places for calculation display (keep trailing zeros for clarity)
                      const accountToDefaultRateDisplay = accountCurrency !== defaultCurrency 
                        ? (() => {
                            const rounded = Math.round(accountToDefaultRate * 100000) / 100000;
                            return rounded.toFixed(5);
                          })()
                        : "1.0000";
                      
                      // Calculate amount in default currency (result)
                      // Field stores account-to-default rate, convert: expenseAmount -> accountAmount -> defaultAmount
                      const transactionAmount = formData.amount ? parseFloat(formData.amount) : null;
                      const exchangeRate = formData.rate ? parseFloat(formData.rate) : null;
                      const accountToDefaultRateForCalc = currentDefaultRate !== null && !isNaN(currentDefaultRate) ? currentDefaultRate : accountToDefaultRate;
                      
                      let amountInDefaultCurrency: number | null = null;
                      if (transactionAmount && exchangeRate && !isNaN(exchangeRate) && exchangeRate > 0) {
                        const amountInAccountCurrency = transactionAmount * exchangeRate;
                        if (accountToDefaultRateForCalc && !isNaN(accountToDefaultRateForCalc) && accountToDefaultRateForCalc > 0) {
                          amountInDefaultCurrency = amountInAccountCurrency * accountToDefaultRateForCalc;
                        }
                      }
                      const defaultDecimalPlaces = getCurrencyDecimalPlaces(defaultCurrency, currencies, defaultCurrency);
                      const transactionDecimalPlaces = getCurrencyDecimalPlaces(formData.currency, currencies, defaultCurrency);
                      
                      // Build helper text: "1 EUR = 444.4444 AMD | €0.85 x 444.44... = AMD 376.0"
                      const rateText = `1 ${accountCurrency} = ${accountToDefaultRateDisplay} ${defaultCurrency}`;
                      let calculationText = "";
                      if (transactionAmount && exchangeRate && accountToDefaultRateForCalc && amountInDefaultCurrency !== null) {
                        // Show calculation: expenseAmount (converted to account currency) x rate = result
                        const amountInAccountCurrency = transactionAmount * exchangeRate;
                        const accountAmountFormatted = formatCurrency(amountInAccountCurrency, accountCurrency, getCurrencyDecimalPlaces(accountCurrency, currencies, defaultCurrency));
                        calculationText = ` | ${accountAmountFormatted} x ${accountToDefaultRateDisplay} = ${formatCurrency(amountInDefaultCurrency, defaultCurrency, defaultDecimalPlaces)}`;
                      } else if (amountInDefaultCurrency !== null) {
                        calculationText = ` | = ${formatCurrency(amountInDefaultCurrency, defaultCurrency, defaultDecimalPlaces)}`;
                      }
                      return rateText + calculationText;
                    })()}
                    InputProps={{
                      endAdornment: (
                        <RefreshIconButton
                          onClick={handleRefreshDefaultCurrencyRate}
                          disabled={shouldDisableDefaultRefresh}
                          disabledTooltip={defaultRateMatchesSetting ? "Rate matches the setting rate." : "Click to update rate to current currency settings"}
                          tooltip="Refresh rate from currency settings"
                        />
                      ),
                      sx: {
                        "& input[type=number]": {
                          MozAppearance: "textfield",
                        },
                        "& input[type=number]::-webkit-outer-spin-button": {
                          WebkitAppearance: "none",
                          margin: 0,
                        },
                        "& input[type=number]::-webkit-inner-spin-button": {
                          WebkitAppearance: "none",
                          margin: 0,
                        },
                      },
                    }}
                  />
                );
              })()}

              {/* Category */}
              <Autocomplete
                fullWidth
                options={(dialogType === "expense" ? expenseCategories : incomeCategories)
                  .sort((a, b) => {
                    const getGroupName = (category: TransactionCategory): string => {
                      if (!category.groupId) return "Ungrouped";
                      const group = categoryGroups.find((g) => g.id === category.groupId);
                      return group?.name || "Ungrouped";
                    };
                    const groupA = getGroupName(a);
                    const groupB = getGroupName(b);
                    if (groupA !== groupB) {
                      return groupA.localeCompare(groupB);
                    }
                    return a.name.localeCompare(b.name);
                  })}
                getOptionLabel={(option) => typeof option === "string" ? option : option.name}
                value={
                  formData.category
                    ? (dialogType === "expense" ? expenseCategories : incomeCategories).find(
                        (cat) => cat.name === formData.category
                      ) || null
                    : null
                }
                groupBy={(option: TransactionCategory) => {
                  if (!option.groupId) return "Ungrouped";
                  const group = categoryGroups.find((g) => g.id === option.groupId);
                  return group?.name || "Ungrouped";
                }}
                ListboxProps={{
                  onWheel: (e: React.WheelEvent) => {
                    const listbox = e.currentTarget;
                    if (listbox.scrollHeight <= listbox.clientHeight) return;
                    e.stopPropagation();
                    e.preventDefault();
                    listbox.scrollTop += e.deltaY;
                  },
                }}
                onChange={(_, newValue) => {
                  const categoryName = typeof newValue === "string" ? newValue : newValue?.name || "";
                  const selectedCategory = typeof newValue === "string" 
                    ? null 
                    : (dialogType === "expense" ? expenseCategories : incomeCategories).find(
                        (cat) => cat.id === newValue?.id
                      ) || null;
                  
                  if (!selectedCategory) {
                    setSelectedCategoryDetails([]);
                    setFormData({ 
                      ...formData, 
                      category: categoryName,
                      details: undefined,
                    });
                    return;
                  }
                  
                  setFormData(prev => ({ 
                    ...prev, 
                    category: categoryName,
                  }));
                  
                  deriveCategoryDetails(categoryName, false);
                }}
                filterOptions={(options: TransactionCategory[], { inputValue }) => {
                  const searchValue = inputValue.toLowerCase();
                  return options.filter((option) => {
                    const categoryNameMatch = option.name.toLowerCase().includes(searchValue);
                    if (categoryNameMatch) return true;
                    
                    if (option.groupId) {
                      const group = categoryGroups.find((g) => g.id === option.groupId);
                      if (group && group.name.toLowerCase().includes(searchValue)) {
                        return true;
                      }
                    }
                    return false;
                  });
                }}
                renderOption={(props, option: TransactionCategory) => (
                  <Box component="li" {...props} key={option.id} sx={{ pl: 3 }}>
                    <Typography variant="body2">
                      {option.name}
                    </Typography>
                  </Box>
                )}
                renderGroup={(params) => (
                  <li key={params.key}>
                    <Box sx={{ position: "sticky", top: 0, bgcolor: "background.paper", zIndex: 1, py: 0.5, px: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                        {params.group}
                      </Typography>
                    </Box>
                    <Box component="ul" sx={{ p: 0 }}>
                      {params.children}
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Category" required />
                )}
              />

              {/* Detail fields - shown only when category is selected AND has enabled details */}
              <Collapse 
                in={!!formData.category && selectedCategoryDetails.length > 0} 
                timeout={300}
                sx={{ 
                  margin: 0,
                  "&.MuiCollapse-hidden": {
                    margin: 0,
                    minHeight: 0,
                  }
                }}
              >
                <Box sx={{ minHeight: (!!formData.category && selectedCategoryDetails.length > 0) ? 'auto' : 0, transition: 'min-height 0.3s ease' }}>
                  {!!formData.category && selectedCategoryDetails.length > 0 && (
                    <Box
                      sx={{
                        mt: 1.5,
                        ml: 2.5,
                        pl: 3,
                        position: "relative",
                        animation: "slideDown 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                        "@keyframes slideDown": {
                          from: {
                            opacity: 0,
                            transform: "translateY(-8px)",
                          },
                          to: {
                            opacity: 1,
                            transform: "translateY(0)",
                          },
                        },
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          left: "0px",
                          top: "-16px",
                          width: "2px",
                          height: "100%",
                          background: "linear-gradient(to bottom, rgba(25, 118, 210, 0.4), rgba(25, 118, 210, 0.6))",
                          borderRadius: "1px",
                          zIndex: 0,
                        },
                      }}
                    >
                      <Stack spacing={2.5}>
                        {selectedCategoryDetails.map((detail, index) => {
                          const detailValue = formData.details?.[detail.name];
                          
                          if (detail.type === "list" && detail.listItems && detail.listItems.length > 0) {
                            const newlyAddedOptions = newlyAddedDetailOptions[detail.name] || [];
                            const allListItems = [...detail.listItems, ...newlyAddedOptions];
                            const stringValue = typeof detailValue === "string" ? detailValue : (typeof detailValue === "number" ? String(detailValue) : "");
                            
                            return (
                              <Box
                                key={detail.name}
                                sx={{
                                  position: "relative",
                                  animation: `fadeInBranch 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.1}s both`,
                                  "@keyframes fadeInBranch": {
                                    from: {
                                      opacity: 0,
                                      transform: "translateX(-8px)",
                                    },
                                    to: {
                                      opacity: 1,
                                      transform: "translateX(0)",
                                    },
                                  },
                                  "&::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: "-20px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    width: "18px",
                                    height: "2px",
                                    background: "linear-gradient(to right, rgba(25, 118, 210, 0.6), rgba(25, 118, 210, 0.4))",
                                    borderRadius: "0 1px 1px 0",
                                    zIndex: 1,
                                  },
                                }}
                              >
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, width: "100%", position: "relative" }}>
                                  <Autocomplete
                                    freeSolo
                                    disableClearable
                                    options={allListItems}
                                    value={stringValue || undefined}
                                    inputValue={detailInputValues[detail.name] || ""}
                                    open={openDetailDropdowns.has(detail.name)}
                                    onOpen={() => {
                                      setOpenDetailDropdowns(prev => new Set(prev).add(detail.name));
                                    }}
                                    onClose={() => {
                                      setOpenDetailDropdowns(prev => {
                                        const newSet = new Set(prev);
                                        newSet.delete(detail.name);
                                        return newSet;
                                      });
                                    }}
                                    onInputChange={(_, newInputValue) => {
                                      setDetailInputValues({
                                        ...detailInputValues,
                                        [detail.name]: newInputValue,
                                      });
                                    }}
                                    onChange={(_, newValue) => {
                                      if (typeof newValue === "string") {
                                        if (newValue === "__CREATE__") {
                                          const inputValue = detailInputValues[detail.name] || "";
                                          handleCreateDetailOption(detail.name, inputValue);
                                        } else if (allListItems.includes(newValue)) {
                                          setFormData({
                                            ...formData,
                                            details: {
                                              ...formData.details,
                                              [detail.name]: newValue,
                                            },
                                          });
                                          setDetailInputValues({
                                            ...detailInputValues,
                                            [detail.name]: "",
                                          });
                                          setOpenDetailDropdowns(prev => {
                                            const newSet = new Set(prev);
                                            newSet.delete(detail.name);
                                            return newSet;
                                          });
                                        }
                                      } else {
                                        setFormData({
                                          ...formData,
                                          details: {
                                            ...formData.details,
                                            [detail.name]: "",
                                          },
                                        });
                                        setDetailInputValues({
                                          ...detailInputValues,
                                          [detail.name]: "",
                                        });
                                      }
                                    }}
                                    filterOptions={(options, params) => {
                                      const { inputValue: filterInputValue } = params;
                                      
                                      if (!filterInputValue.trim()) {
                                        return options;
                                      }
                                      
                                      const filtered = options.filter((option) =>
                                        option.toLowerCase().includes(filterInputValue.toLowerCase())
                                      );
                                      
                                      const trimmedInput = filterInputValue.trim().toLowerCase();
                                      const matchesExisting = filtered.some(option => option.toLowerCase() === trimmedInput);
                                      
                                      if (!matchesExisting) {
                                        return [...filtered, "__CREATE__"];
                                      }
                                      
                                      return filtered;
                                    }}
                                    getOptionLabel={(option) => {
                                      if (option === "__CREATE__") {
                                        return "";
                                      }
                                      return option;
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        label={detail.name}
                                        placeholder="Type to search or create..."
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter" && detailInputValues[detail.name]?.trim()) {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            const inputValue = detailInputValues[detail.name] || "";
                                            handleCreateDetailOption(detail.name, inputValue);
                                          }
                                        }}
                                        sx={{
                                          "& .MuiOutlinedInput-root": {
                                            backgroundColor: "rgba(25, 118, 210, 0.04)",
                                            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                                            "&:hover": {
                                              backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            },
                                            "&.Mui-focused": {
                                              backgroundColor: "rgba(25, 118, 210, 0.06)",
                                            },
                                          },
                                        }}
                                        InputProps={{
                                          ...params.InputProps,
                                          endAdornment: (
                                            <>
                                              {stringValue && newlyAddedOptions.includes(stringValue) && !openDetailDropdowns.has(detail.name) && (
                                                <Tooltip
                                                  title={`Will be saved to "${detail.name}" list in settings`}
                                                  arrow
                                                  placement="top"
                                                >
                                                  <IconButton
                                                    size="small"
                                                    sx={{
                                                      mr: 0.5,
                                                      color: "info.main",
                                                      "&:hover": {
                                                        backgroundColor: "rgba(25, 118, 210, 0.08)",
                                                      },
                                                    }}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                    }}
                                                  >
                                                    <InfoIcon fontSize="small" />
                                                  </IconButton>
                                                </Tooltip>
                                              )}
                                              <InputAdornment position="end">
                                                <ArrowDropDownIcon sx={{ color: "action.active", pointerEvents: "none" }} />
                                              </InputAdornment>
                                            </>
                                          ),
                                        }}
                                      />
                                    )}
                                    renderOption={(props, option) => {
                                      if (option === "__CREATE__") {
                                        const inputValue = detailInputValues[detail.name] || "";
                                        return (
                                          <Box
                                            component="li"
                                            {...props}
                                            key="__CREATE__"
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 1,
                                              width: "100%",
                                              py: 1,
                                              px: 2,
                                              cursor: "pointer",
                                              "&:hover": {
                                                backgroundColor: "action.hover",
                                              },
                                            }}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              handleCreateDetailOption(detail.name, inputValue);
                                            }}
                                          >
                                            <AddIcon fontSize="small" color="primary" />
                                            <Typography variant="body2" color="primary">
                                              Create "{inputValue}"
                                            </Typography>
                                          </Box>
                                        );
                                      }
                                      const isNewlyAdded = newlyAddedOptions.includes(option);
                                      return (
                                        <Box 
                                          component="li" 
                                          {...props} 
                                          key={option}
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            width: "100%",
                                            py: 1,
                                            px: 2,
                                            "&:hover": {
                                              backgroundColor: "action.hover",
                                            },
                                          }}
                                        >
                                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: 1 }}>
                                            <Typography variant="body2" sx={{ flex: 1 }}>
                                              {option}
                                            </Typography>
                                            {isNewlyAdded && (
                                              <Tooltip
                                                title={`Will be saved to "${detail.name}" list in settings`}
                                                arrow
                                                placement="top"
                                              >
                                                <InfoIcon 
                                                  fontSize="small" 
                                                  sx={{ 
                                                    color: "info.main",
                                                    fontSize: "16px",
                                                    cursor: "help"
                                                  }} 
                                                />
                                              </Tooltip>
                                            )}
                                          </Box>
                                          {isNewlyAdded && (
                                            <IconButton
                                              size="small"
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                const updatedOptions = (newlyAddedDetailOptions[detail.name] || []).filter(opt => opt !== option);
                                                setNewlyAddedDetailOptions({
                                                  ...newlyAddedDetailOptions,
                                                  [detail.name]: updatedOptions,
                                                });
                                                if (formData.details?.[detail.name] === option) {
                                                  setFormData({
                                                    ...formData,
                                                    details: {
                                                      ...formData.details,
                                                      [detail.name]: "",
                                                    },
                                                  });
                                                  setDetailInputValues({
                                                    ...detailInputValues,
                                                    [detail.name]: "",
                                                  });
                                                }
                                              }}
                                              sx={{
                                                ml: 1,
                                                color: "error.main",
                                                "&:hover": {
                                                  backgroundColor: "rgba(211, 47, 47, 0.08)",
                                                },
                                              }}
                                            >
                                              <DeleteIcon fontSize="small" />
                                            </IconButton>
                                          )}
                                        </Box>
                                      );
                                    }}
                                    sx={{ flex: 1, minWidth: 0 }}
                                    ListboxProps={{
                                      sx: {
                                        zIndex: 1400,
                                      },
                                      onWheel: (e: React.WheelEvent) => {
                                        const listbox = e.currentTarget;
                                        if (listbox.scrollHeight <= listbox.clientHeight) return;
                                        e.stopPropagation();
                                        e.preventDefault();
                                        listbox.scrollTop += e.deltaY;
                                      },
                                    }}
                                  />
                                </Box>
                              </Box>
                            );
                          } else if (detail.type === "measurable") {
                            return (
                              <Box
                                key={detail.name}
                                sx={{
                                  position: "relative",
                                  animation: `fadeInBranch 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.1}s both`,
                                  "@keyframes fadeInBranch": {
                                    from: {
                                      opacity: 0,
                                      transform: "translateX(-8px)",
                                    },
                                    to: {
                                      opacity: 1,
                                      transform: "translateX(0)",
                                    },
                                  },
                                  "&::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: "-20px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    width: "18px",
                                    height: "2px",
                                    background: "linear-gradient(to right, rgba(25, 118, 210, 0.6), rgba(25, 118, 210, 0.4))",
                                    borderRadius: "0 1px 1px 0",
                                    zIndex: 1,
                                  },
                                }}
                              >
                                <TextField
                                  fullWidth
                                  sx={{ 
                                    "& .MuiOutlinedInput-root": {
                                      backgroundColor: "rgba(25, 118, 210, 0.04)",
                                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                                      "&:hover": {
                                        backgroundColor: "rgba(25, 118, 210, 0.08)",
                                      },
                                      "&.Mui-focused": {
                                        backgroundColor: "rgba(25, 118, 210, 0.06)",
                                      },
                                    },
                                  }}
                                  label={detail.name}
                                  type="number"
                                  inputProps={{ step: "any", min: "0" }}
                                  value={detailValue !== undefined ? String(detailValue) : ""}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    const numValue = value === "" ? undefined : parseFloat(value);
                                    setFormData({
                                      ...formData,
                                      details: {
                                        ...formData.details,
                                        [detail.name]: numValue !== undefined ? numValue : "",
                                      },
                                    });
                                  }}
                                  InputProps={{
                                    endAdornment: detail.unit ? (
                                      <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                                        {detail.unit}
                                      </Typography>
                                    ) : undefined,
                                    sx: {
                                      "& input[type=number]": {
                                        MozAppearance: "textfield",
                                      },
                                      "& input[type=number]::-webkit-outer-spin-button": {
                                        WebkitAppearance: "none",
                                        margin: 0,
                                      },
                                      "& input[type=number]::-webkit-inner-spin-button": {
                                        WebkitAppearance: "none",
                                        margin: 0,
                                      },
                                    },
                                  }}
                                />
                              </Box>
                            );
                          }
                          return null;
                        })}
                      </Stack>
                    </Box>
                  )}
                </Box>
              </Collapse>

              {/* Custom fields (workspace-configured; only for this transaction type) */}
              {expenseIncomeFieldsForType.length > 0 && (
                <Box sx={{ pt: 1 }}>
                  <Stack spacing={1.5}>
                    {expenseIncomeFieldsForType.map((field) => {
                      const value = formData.customFieldValues?.[field.id];
                      const pendingFile = formData.customFieldPendingFiles?.[field.id];
                      const attachmentValue = value && typeof value === "object" && "url" in value ? (value as CustomFieldAttachmentValue) : null;
                      const displayName = pendingFile?.name ?? attachmentValue?.name ?? "";
                      if (field.type === "text") {
                        return (
                          <TextField
                            key={field.id}
                            label={
                              <Stack direction="row" alignItems="center" gap={0.5}>
                                <TransactionFieldIcon icon={field.icon} />
                                <span>{field.label}</span>
                              </Stack>
                            }
                            fullWidth
                            value={typeof value === "string" ? value : ""}
                            onChange={(e) => {
                              setFormData((prev) => ({
                                ...prev,
                                customFieldValues: {
                                  ...(prev.customFieldValues || {}),
                                  [field.id]: e.target.value,
                                },
                              }));
                            }}
                            disabled={isSubmitting}
                          />
                        );
                      }
                      return (
                        <TextField
                          key={field.id}
                          fullWidth
                          label={
                            <Stack direction="row" alignItems="center" gap={0.5}>
                              <TransactionFieldIcon icon={field.icon} />
                              <span>{field.label}</span>
                            </Stack>
                          }
                          value={displayName}
                          placeholder={`${field.label} (PDF, PNG, JPG…) · Max 10MB`}
                          disabled={isSubmitting}
                          onClick={() => {
                            if (!isSubmitting) {
                              customFieldFileTargetRef.current = field.id;
                              customFieldFileInputRef.current?.click();
                            }
                          }}
                          InputProps={{
                            readOnly: true,
                            startAdornment: displayName ? (
                              <InputAdornment position="start" sx={{ mr: 0 }}>
                                <Tooltip title={attachmentValue?.url ? "Preview" : displayName}>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (attachmentValue?.url) openAttachment(attachmentValue.url);
                                    }}
                                    sx={{ p: 0.5 }}
                                    color="primary"
                                    disabled={!attachmentValue?.url}
                                  >
                                    <AttachmentIcon
                                      attachmentType={attachmentValue?.type ?? pendingFile?.type}
                                      attachmentName={displayName}
                                    />
                                  </IconButton>
                                </Tooltip>
                              </InputAdornment>
                            ) : null,
                            endAdornment: (
                              <InputAdornment position="end">
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                  {displayName ? (
                                    <>
                                      {attachmentValue?.url && (
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openAttachment(attachmentValue!.url);
                                          }}
                                          color="primary"
                                        >
                                          <DownloadIcon fontSize="small" />
                                        </IconButton>
                                      )}
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          customFieldFileTargetRef.current = field.id;
                                          customFieldFileInputRef.current?.click();
                                        }}
                                        disabled={isSubmitting}
                                      >
                                        <AttachFileIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setFormData((prev) => {
                                            const next = { ...prev };
                                            if (next.customFieldPendingFiles?.[field.id]) {
                                              const { [field.id]: _, ...rest } = next.customFieldPendingFiles;
                                              next.customFieldPendingFiles = Object.keys(rest).length ? rest : undefined;
                                            }
                                            if (next.customFieldValues?.[field.id]) {
                                              const cv = { ...(next.customFieldValues || {}) };
                                              delete cv[field.id];
                                              next.customFieldValues = Object.keys(cv).length ? cv : undefined;
                                            }
                                            return next;
                                          });
                                        }}
                                        disabled={isSubmitting}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </>
                                  ) : (
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        customFieldFileTargetRef.current = field.id;
                                        customFieldFileInputRef.current?.click();
                                      }}
                                      disabled={isSubmitting}
                                      color="primary"
                                    >
                                      <AttachFileIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                </Box>
                              </InputAdornment>
                            ),
                          }}
                          helperText={undefined}
                          sx={{ cursor: isSubmitting ? "default" : "pointer" }}
                        />
                      );
                    })}
                  </Stack>
                  <input
                    type="file"
                    ref={customFieldFileInputRef}
                    style={{ display: "none" }}
                    accept="image/*,.pdf"
                    onChange={(e) => {
                      const fieldId = customFieldFileTargetRef.current;
                      const file = e.target.files?.[0];
                      if (fieldId && file) {
                        setFormData((prev) => ({
                          ...prev,
                          customFieldPendingFiles: {
                            ...(prev.customFieldPendingFiles || {}),
                            [fieldId]: file,
                          },
                        }));
                      }
                      e.target.value = "";
                      customFieldFileTargetRef.current = null;
                    }}
                  />
                </Box>
              )}

            </Stack>
          </form>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, pt: 1, gap: 1, display: "flex" }}>
          {viewOnly ? (
            <>
              <Button onClick={handleDialogClose} variant="outlined" sx={{ flex: 1, py: 1.5 }}>
                Close
              </Button>
              <Button variant="contained" startIcon={<EditIcon />} onClick={onSwitchToEdit} sx={{ flex: 1, py: 1.5 }}>
                Edit transaction
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleDialogClose} variant="outlined" sx={{ flex: 1, py: 1.5 }}>
                Cancel
              </Button>
              <Button
                type="submit"
                form="transaction-form"
                variant="contained"
                color={dialogType === "income" ? "success" : "error"}
                sx={{ flex: 1, py: 1.5 }}
                disabled={isSubmitting || (editingTransaction ? !hasTransactionChanges() : !isTransactionFormValid)}
              >
                {editingTransaction ? "Update" : "Add"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog
        open={transferDialogOpen}
        onClose={handleTransferDialogClose}
        maxWidth="sm"
        fullWidth
        disableEnforceFocus={false}
        disableAutoFocus={false}
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
            <Stack direction="row" spacing={2} alignItems="baseline" sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6">
                {editingTransaction && editingTransaction.type === "transfer"
                  ? (viewOnly ? "View" : "Edit") + " Transfer"
                  : "Transfer Between Accounts"}
              </Typography>
              {((editingTransaction?.type === "transfer" && getTransactionFriendlyId(transactionIdPrefix, editingTransaction.friendlyIdSequence)) ||
                (!editingTransaction && transactionIdPrefix && transactionIdNextSequence != null && getTransactionFriendlyId(transactionIdPrefix, transactionIdNextSequence))) && (
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "baseline",
                    ml: 1,
                    "& .copy-id-btn": { opacity: 0, transition: "opacity 0.15s ease" },
                    "&:hover .copy-id-btn, &:focus-within .copy-id-btn": { opacity: 1 },
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    ID: {editingTransaction?.type === "transfer"
                      ? getTransactionFriendlyId(transactionIdPrefix, editingTransaction.friendlyIdSequence)
                      : getTransactionFriendlyId(transactionIdPrefix, transactionIdNextSequence)}
                  </Typography>
                  <CopyFeedbackTooltip copyLabel="Copy ID" feedbackKey={editingTransaction ? `dialog-transfer-id-${editingTransaction.id}` : "dialog-transfer-id-new"} lastCopiedKey={lastCopiedKey} isMobile={false}>
                    <IconButton
                      className="copy-id-btn"
                      size="small"
                      onClick={() => {
                        const id = editingTransaction?.type === "transfer"
                          ? getTransactionFriendlyId(transactionIdPrefix, editingTransaction.friendlyIdSequence)
                          : getTransactionFriendlyId(transactionIdPrefix, transactionIdNextSequence);
                        if (id) copyToClipboard(id).then((ok) => ok && showCopiedFeedback(editingTransaction ? `dialog-transfer-id-${editingTransaction.id}` : "dialog-transfer-id-new"));
                      }}
                      sx={{ p: 0.25, ml: 0.25 }}
                      aria-label="Copy transaction ID"
                    >
                      <ContentCopyIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </CopyFeedbackTooltip>
                </Box>
              )}
            </Stack>
            <IconButton size="small" onClick={handleTransferDialogClose} sx={{ flexShrink: 0 }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <form onSubmit={handleTransferSubmit} id="transfer-form">
            <Stack component="fieldset" spacing={2} sx={{ border: "none", padding: 0, margin: 0, marginTop: "10px", minWidth: 0 }} disabled={viewOnly}>
              {/* Date and Time - Display uses workspace date format (MM/DD or DD/MM). */}
              <input
                type="date"
                ref={transferDateInputRef}
                value={transferFormData.date}
                onChange={(e) => setTransferFormData({ ...transferFormData, date: e.target.value })}
                aria-hidden
                tabIndex={-1}
                style={{ position: "absolute", opacity: 0, pointerEvents: "none", width: 0, height: 0 }}
              />
              <TextField
                required
                label="Date"
                type="text"
                value={formatDateForDisplay(transferFormData.date, dateFormat)}
                onClick={() => transferDateInputRef.current?.showPicker?.()}
                inputProps={{
                  style: { cursor: "pointer" },
                  readOnly: true,
                }}
                sx={{ width: "100%" }}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          transferDateInputRef.current?.showPicker?.();
                        }}
                        sx={{ 
                          color: "text.secondary",
                          "&:hover": { bgcolor: "action.hover" },
                        }}
                      >
                        <CalendarTodayIcon sx={{ fontSize: "1.25rem" }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      {!showTransferTime ? (
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            const now = getCurrentTime();
                            setShowTransferTime(true);
                            setTransferFormData({ ...transferFormData, time: now });
                            if (timeFormat === "12h") {
                              setTransferTimeInputValue(now ? formatTimeForDisplay(now) : "");
                            }
                          }}
                          sx={{
                            minWidth: "auto",
                            px: 1,
                            py: 0.5,
                            color: "text.primary",
                            fontWeight: "normal",
                            textTransform: "none",
                            "&:hover": {
                              bgcolor: "action.hover",
                            },
                          }}
                        >
                          Time
                        </Button>
                      ) : (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* Time field: 12h shows "h:mm AM/PM", 24h shows "HH:mm" per workspace setting */}
                          <TextField
                            inputRef={transferTimeInputRef}
                            type="text"
                            value={transferTimeInputValue}
                            onChange={(e) => {
                              const inputValue = e.target.value;
                              setTransferTimeInputValue(inputValue);
                              const newTime24 = parseTimeInput(inputValue.trim());
                              if (newTime24) {
                                setTransferFormData({ ...transferFormData, time: newTime24 });
                              } else if (!inputValue.trim()) {
                                setTransferFormData({ ...transferFormData, time: "" });
                              }
                            }}
                            onBlur={(e) => {
                              const inputValue = e.target.value.trim();
                              if (inputValue) {
                                const newTime24 = parseTimeInput(inputValue);
                                if (newTime24) {
                                  setTransferFormData({ ...transferFormData, time: newTime24 });
                                  setTransferTimeInputValue(formatTimeForDisplay(newTime24));
                                } else {
                                  setTransferFormData({ ...transferFormData, time: "" });
                                  setTransferTimeInputValue("");
                                }
                              } else {
                                setTransferFormData({ ...transferFormData, time: "" });
                                setTransferTimeInputValue("");
                              }
                            }}
                            placeholder={timeFormat === "12h" ? "h:mm AM/PM" : "HH:mm"}
                            size="small"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            sx={{ 
                              width: timeFormat === "12h" ? 120 : 100,
                              "& .MuiOutlinedInput-root": {
                                height: 32,
                                paddingLeft: 0,
                                paddingRight: 0,
                                "& fieldset": {
                                  borderColor: "rgba(0, 0, 0, 0.23) !important",
                                },
                              },
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              setTransferFormData({ ...transferFormData, time: "" });
                              setShowTransferTime(false);
                            }}
                            sx={{
                              color: "text.secondary",
                              "&:hover": {
                                color: "text.primary",
                                bgcolor: "action.hover",
                              },
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </InputAdornment>
                  ),
                }}
              />

              {/* Amount */}
              {(() => {
                // Get currency from fromAccount
                const fromAccount = transferFormData.fromAccountId 
                  ? accounts.find((acc) => acc.id === transferFormData.fromAccountId && !acc.isArchived)
                  : null;
                const transferCurrency = fromAccount?.currency || defaultCurrency;
                
                // Validate decimal places
                const validation = validateAmountDecimalPlaces(
                  transferFormData.amount,
                  transferCurrency,
                  currencies,
                  defaultCurrency
                );
                
                // Calculate converted amount for helperText when currencies differ
                const calculationHelperText = (() => {
                  if (!transferFormData.fromAccountId || !transferFormData.toAccountId || !transferFormData.amount) return null;
                  
                  const fromAccount = accounts.find((acc) => acc.id === transferFormData.fromAccountId && !acc.isArchived);
                  const toAccount = accounts.find((acc) => acc.id === transferFormData.toAccountId && !acc.isArchived);
                  if (!fromAccount || !toAccount) return null;
                  
                  const currenciesDiffer = fromAccount.currency !== toAccount.currency;
                  if (!currenciesDiffer) return null;
                  
                  const transferAmount = parseFloat(transferFormData.amount);
                  if (isNaN(transferAmount) || transferAmount <= 0) return null;
                  
                  // Get the rate to use: stored rate if valid, otherwise calculate it
                  const storedRate = transferFormData.transferRate ? parseFloat(transferFormData.transferRate) : null;
                  const calculatedRate = calculateTransferRate(fromAccount.currency, toAccount.currency);
                  
                  // Use stored rate if it's valid and reasonable, otherwise use calculated rate
                  // Validate rate: should be between 0.0001 and 10000 (covers most currency conversions)
                  const isValidStoredRate = storedRate !== null && !isNaN(storedRate) && storedRate > 0 && storedRate >= 0.0001 && storedRate <= 10000;
                  const rateToUse = isValidStoredRate ? storedRate : calculatedRate;
                  
                  // Final validation: ensure calculated rate is also reasonable
                  if (!rateToUse || isNaN(rateToUse) || rateToUse <= 0 || rateToUse < 0.0001 || rateToUse > 10000) {
                    return null;
                  }
                  
                  const convertedAmount = transferAmount * rateToUse;
                  
                  if (isNaN(convertedAmount) || convertedAmount <= 0) return null;
                  
                  const fromAccountDecimalPlaces = getCurrencyDecimalPlaces(fromAccount.currency, currencies, defaultCurrency);
                  const toAccountDecimalPlaces = getCurrencyDecimalPlaces(toAccount.currency, currencies, defaultCurrency);
                  const formattedAmount = formatCurrency(transferAmount, fromAccount.currency, fromAccountDecimalPlaces);
                  const formattedConverted = formatCurrency(convertedAmount, toAccount.currency, toAccountDecimalPlaces);
                  
                  return `Calculation: ${formattedAmount} = ${formattedConverted}`;
                })();
                
                // Combine helper text: validation error, currency info, and calculation
                const currencyHelperText = transferFormData.fromAccountId
                  ? `In ${transferCurrency}`
                  : undefined;
                
                const helperTextParts = [];
                if (validation.errorMessage) helperTextParts.push(validation.errorMessage);
                if (calculationHelperText) helperTextParts.push(calculationHelperText);
                if (!validation.errorMessage && !calculationHelperText && currencyHelperText) helperTextParts.push(currencyHelperText);
                
                const combinedHelperText = helperTextParts.length > 0 ? helperTextParts.join(" | ") : undefined;
                
                return (
                  <TextField
                    required
                    label="Amount"
                    type="number"
                    inputProps={{ step: "any", min: "0" }}
                    value={transferFormData.amount}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Only allow digits and a single decimal point
                      // Reject scientific notation, negative signs, and other invalid characters
                      if (value === "" || /^\d*\.?\d*$/.test(value)) {
                        setTransferFormData({ ...transferFormData, amount: value });
                      }
                    }}
                    onPaste={(e) => {
                      e.preventDefault();
                      const pastedText = e.clipboardData.getData('text');
                      // Sanitize pasted content: remove all non-digit and non-decimal characters
                      // Also remove scientific notation, negative signs, etc.
                      const sanitized = pastedText.replace(/[^\d.]/g, '');
                      // Remove multiple decimal points, keeping only the first one
                      const parts = sanitized.split('.');
                      const cleaned = parts.length > 0 
                        ? parts[0] + (parts.length > 1 ? '.' + parts.slice(1).join('') : '')
                        : '';
                      // Only allow valid number format
                      if (cleaned === "" || /^\d*\.?\d*$/.test(cleaned)) {
                        setTransferFormData({ ...transferFormData, amount: cleaned });
                      }
                    }}
                    onFocus={(e) => {
                      // Convert formatted amount back to raw value for editing
                      const value = e.target.value;
                      if (value && value.trim() !== "") {
                        const rawValue = convertFormattedAmountToRawValue(value);
                        if (rawValue !== value) {
                          setTransferFormData({ ...transferFormData, amount: rawValue });
                        }
                      }
                    }}
                    onBlur={(e) => {
                      // Format amount to match currency decimal places when field loses focus
                      const value = e.target.value;
                      if (value && value.trim() !== "") {
                        const formatted = formatAmountToCurrencyDecimalPlaces(
                          value,
                          transferCurrency,
                          currencies,
                          defaultCurrency
                        );
                        setTransferFormData({ ...transferFormData, amount: formatted });
                      }
                    }}
                    fullWidth
                    placeholder="0.00"
                    error={!validation.isValid}
                    helperText={combinedHelperText}
                    InputProps={{
                      sx: {
                        "& input[type=number]": {
                          MozAppearance: "textfield",
                        },
                        "& input[type=number]::-webkit-outer-spin-button": {
                          WebkitAppearance: "none",
                          margin: 0,
                        },
                        "& input[type=number]::-webkit-inner-spin-button": {
                          WebkitAppearance: "none",
                          margin: 0,
                        },
                      },
                    }}
                  />
                );
              })()}

              {/* Fee Fields - Show when feeAmount is set (only when transfer fee is enabled) */}
              {allowTransferFee && transferFormData.feeAmount !== undefined && (() => {
                const transferFeeAccount = transferFormData.feeAccountId ? accounts.find((acc) => acc.id === transferFormData.feeAccountId && !acc.isArchived) : null;
                const transferFeeCurrency = transferFeeAccount?.currency || defaultCurrency;
                const transferFeeAmountValidation = validateAmountDecimalPlaces(
                  transferFormData.feeAmount ?? "",
                  transferFeeCurrency,
                  currencies,
                  defaultCurrency
                );
                return (
                <Stack direction="row" spacing={2}>
                  <TextField
                    required
                    label="Fee Amount"
                    type="number"
                    inputProps={{ step: "any", min: "0" }}
                    value={transferFormData.feeAmount || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "" || /^\d*\.?\d*$/.test(value)) {
                        setTransferFormData({ ...transferFormData, feeAmount: value });
                      }
                    }}
                    onPaste={(e) => {
                      e.preventDefault();
                      const pastedText = e.clipboardData.getData("text");
                      const sanitized = pastedText.replace(/[^\d.]/g, "");
                      const parts = sanitized.split(".");
                      const cleaned = parts.length > 0 ? parts[0] + (parts.length > 1 ? "." + parts.slice(1).join("") : "") : "";
                      if (cleaned === "" || /^\d*\.?\d*$/.test(cleaned)) {
                        setTransferFormData({ ...transferFormData, feeAmount: cleaned });
                      }
                    }}
                    onFocus={(e) => {
                      const value = e.target.value;
                      if (value && value.trim() !== "") {
                        const rawValue = convertFormattedAmountToRawValue(value);
                        if (rawValue !== value) {
                          setTransferFormData({ ...transferFormData, feeAmount: rawValue });
                        }
                      }
                    }}
                    onBlur={(e) => {
                      // Only format to currency decimal places when user has selected a fee account
                      if (!transferFormData.feeAccountId) return;
                      const value = e.target.value;
                      if (value && value.trim() !== "") {
                        const formatted = formatAmountToCurrencyDecimalPlaces(value, transferFeeCurrency, currencies, defaultCurrency);
                        setTransferFormData({ ...transferFormData, feeAmount: formatted });
                      }
                    }}
                    onKeyPress={(e) => {
                      if (e.key === "e" || e.key === "E" || e.key === "+" || e.key === "-") e.preventDefault();
                    }}
                    error={transferFormData.feeAccountId ? !transferFeeAmountValidation.isValid : false}
                    helperText={transferFormData.feeAccountId ? (transferFeeAmountValidation.errorMessage || undefined) : undefined}
                    sx={{ flex: 1 }}
                    placeholder="0.00"
                    InputProps={{
                      endAdornment: transferFormData.feeAccountId ? (
                        <InputAdornment position="end">
                          <Typography variant="body2" color="text.secondary">
                            {transferFeeAccount?.currency || ""}
                          </Typography>
                        </InputAdornment>
                      ) : undefined,
                      sx: {
                        "& input[type=number]": {
                          MozAppearance: "textfield",
                        },
                        "& input[type=number]::-webkit-outer-spin-button": {
                          WebkitAppearance: "none",
                          margin: 0,
                        },
                        "& input[type=number]::-webkit-inner-spin-button": {
                          WebkitAppearance: "none",
                          margin: 0,
                        },
                      },
                    }}
                  />
                  <Autocomplete
                    sx={{ flex: 1 }}
                    options={accounts
                      .filter((acc) => !acc.isArchived)
                      .sort((a, b) => {
                        if (a.group !== b.group) {
                          return a.group.localeCompare(b.group);
                        }
                        return a.accountName.localeCompare(b.accountName);
                      })}
                    value={transferFormData.feeAccountId ? accounts.find((acc) => acc.id === transferFormData.feeAccountId && !acc.isArchived) || null : null}
                    onChange={(_, newValue: Account | null) => {
                      setTransferFormData({
                        ...transferFormData,
                        feeAccountId: newValue?.id || "",
                      });
                    }}
                    groupBy={(option: Account) => option.group}
                    getOptionLabel={(option: Account) =>
                      `${option.accountName} (${formatCurrency(option.amount || 0, option.currency, getCurrencyDecimalPlaces(option.currency, currencies, defaultCurrency))} ${option.currency})`
                    }
                    ListboxProps={{
                      onWheel: (e: React.WheelEvent) => {
                        const listbox = e.currentTarget;
                        if (listbox.scrollHeight <= listbox.clientHeight) return;
                        e.stopPropagation();
                        e.preventDefault();
                        listbox.scrollTop += e.deltaY;
                      },
                    }}
                    filterOptions={(options: Account[], { inputValue }) => {
                      const searchValue = inputValue.toLowerCase();
                      return options.filter(
                        (option) =>
                          option.accountName.toLowerCase().includes(searchValue) ||
                          option.group.toLowerCase().includes(searchValue)
                      );
                    }}
                    renderOption={(props, option: Account) => (
                      <Box component="li" {...props} key={option.id} sx={{ pl: 3 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%",
                          }}
                        >
                          <Typography variant="body2">{option.accountName}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                            {formatCurrency(option.amount || 0, option.currency, getCurrencyDecimalPlaces(option.currency, currencies, defaultCurrency))} ({option.currency})
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    renderGroup={(params) => (
                      <li key={params.key}>
                        <Box
                          sx={{
                            position: "sticky",
                            top: 0,
                            bgcolor: "background.paper",
                            zIndex: 1,
                            py: 0.5,
                            px: 1,
                          }}
                        >
                          <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                            {params.group}
                          </Typography>
                        </Box>
                        <Box component="ul" sx={{ p: 0 }}>
                          {params.children}
                        </Box>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Fee Account"
                        required
                        helperText={undefined}
                      />
                    )}
                    isOptionEqualToValue={(option: Account, value: Account) => option.id === value.id}
                  />
                </Stack>
                );
              })()}

              {/* From Account */}
              <Autocomplete
                fullWidth
                options={accounts
                  .filter((acc) => !acc.isArchived)
                  .sort((a, b) => {
                    if (a.group !== b.group) {
                      return a.group.localeCompare(b.group);
                    }
                    return a.accountName.localeCompare(b.accountName);
                  })}
                value={accounts.find((acc) => acc.id === transferFormData.fromAccountId && !acc.isArchived) || null}
                onChange={(_, newValue: Account | null) => {
                  setTransferFormData({
                    ...transferFormData,
                    fromAccountId: newValue?.id || "",
                    toAccountId: newValue?.id === transferFormData.toAccountId ? "" : transferFormData.toAccountId,
                  });
                }}
                groupBy={(option: Account) => option.group}
                getOptionLabel={(option: Account) => `${option.accountName} (${formatCurrency(option.amount || 0, option.currency, getCurrencyDecimalPlaces(option.currency, currencies, defaultCurrency))} ${option.currency})`}
                filterOptions={(options: Account[], { inputValue }) => {
                  const searchValue = inputValue.toLowerCase();
                  return options.filter(
                    (option) =>
                      option.accountName.toLowerCase().includes(searchValue) ||
                      option.group.toLowerCase().includes(searchValue)
                  );
                }}
                ListboxProps={{
                  onWheel: (e: React.WheelEvent) => {
                    const listbox = e.currentTarget;
                    if (listbox.scrollHeight <= listbox.clientHeight) return;
                    e.stopPropagation();
                    e.preventDefault();
                    listbox.scrollTop += e.deltaY;
                  },
                }}
                renderOption={(props, option: Account) => (
                  <Box component="li" {...props} key={option.id} sx={{ pl: 3 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                      <Typography variant="body2">
                        {option.accountName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                        {formatCurrency(option.amount || 0, option.currency, getCurrencyDecimalPlaces(option.currency, currencies, defaultCurrency))} ({option.currency})
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderGroup={(params) => (
                  <li key={params.key}>
                    <Box sx={{ position: "sticky", top: 0, bgcolor: "background.paper", zIndex: 1, py: 0.5, px: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                        {params.group}
                      </Typography>
                    </Box>
                    <Box component="ul" sx={{ p: 0 }}>
                      {params.children}
                    </Box>
                  </li>
                )}
                renderInput={(params) => {
                  const fromAccount = transferFormData.fromAccountId ? accounts.find((acc) => acc.id === transferFormData.fromAccountId && !acc.isArchived) : null;
                  const fromAccountCurrency = fromAccount?.currency || defaultCurrency;
                  const transferAmount = parseFloat(transferFormData.amount || "0");
                  
                  const balanceHelperText = fromAccount && transferFormData.amount && !isNaN(transferAmount) && transferAmount > 0
                    ? (() => {
                        // Calculate account balance before transfer (if editing)
                        let balanceBeforeTransfer = fromAccount.amount;
                        if (editingTransaction && editingTransaction.type === "transfer" && editingTransaction.fromAccountId === fromAccount.id) {
                          // Reverse the old transfer: add back the old amount
                          const oldAmount = editingTransaction.amount || 0;
                          balanceBeforeTransfer = fromAccount.amount + oldAmount;
                        }
                        
                        let newBalance = balanceBeforeTransfer - transferAmount;
                        
                        // Check if fee account matches From account and subtract fee
                        let feeAmount: number | undefined;
                        if (transferFormData.feeAmount && transferFormData.feeAccountId && transferFormData.feeAccountId === fromAccount.id) {
                          feeAmount = parseFloat(transferFormData.feeAmount);
                          if (!isNaN(feeAmount) && feeAmount > 0) {
                            newBalance = newBalance - feeAmount;
                          }
                        }
                        
                        const isNegative = newBalance < 0;
                        const balanceText = `Balance after transfer: ${formatCurrency(newBalance, fromAccountCurrency)}`;
                        const feeText = feeAmount ? ` (includes ${formatCurrency(feeAmount, fromAccountCurrency)} fee)` : "";
                        const negativeText = isNegative ? " (negative)" : "";
                        return balanceText + feeText + negativeText;
                      })()
                    : undefined;
                  
                  const balanceHelperColor = fromAccount && transferFormData.amount && !isNaN(transferAmount) && transferAmount > 0
                    ? (() => {
                        // Calculate account balance before transfer (if editing)
                        let balanceBeforeTransfer = fromAccount.amount;
                        if (editingTransaction && editingTransaction.type === "transfer" && editingTransaction.fromAccountId === fromAccount.id) {
                          // Reverse the old transfer: add back the old amount
                          const oldAmount = editingTransaction.amount || 0;
                          balanceBeforeTransfer = fromAccount.amount + oldAmount;
                        }
                        
                        let newBalance = balanceBeforeTransfer - transferAmount;
                        
                        // Check if fee account matches From account and subtract fee
                        if (transferFormData.feeAmount && transferFormData.feeAccountId && transferFormData.feeAccountId === fromAccount.id) {
                          const feeAmount = parseFloat(transferFormData.feeAmount);
                          if (!isNaN(feeAmount) && feeAmount > 0) {
                            newBalance = newBalance - feeAmount;
                          }
                        }
                        
                        return newBalance < 0 ? "warning.main" : "text.secondary";
                      })()
                    : "text.secondary";
                  
                  return (
                    <TextField
                      {...params}
                      label="From Account"
                      required
                      helperText={balanceHelperText}
                      FormHelperTextProps={{
                        sx: { color: balanceHelperColor },
                      }}
                    />
                  );
                }}
                isOptionEqualToValue={(option: Account, value: Account) => option.id === value.id}
              />

              <Autocomplete
                fullWidth
                options={accounts
                  .filter((acc) => acc.id !== transferFormData.fromAccountId && !acc.isArchived)
                  .sort((a, b) => {
                    if (a.group !== b.group) {
                      return a.group.localeCompare(b.group);
                    }
                    return a.accountName.localeCompare(b.accountName);
                  })}
                value={accounts.find((acc) => acc.id === transferFormData.toAccountId && !acc.isArchived) || null}
                onChange={(_, newValue: Account | null) => {
                  setTransferFormData({
                    ...transferFormData,
                    toAccountId: newValue?.id || "",
                  });
                }}
                groupBy={(option: Account) => option.group}
                getOptionLabel={(option: Account) => `${option.accountName} (${formatCurrency(option.amount || 0, option.currency, getCurrencyDecimalPlaces(option.currency, currencies, defaultCurrency))} ${option.currency})`}
                filterOptions={(options: Account[], { inputValue }) => {
                  const searchValue = inputValue.toLowerCase();
                  return options.filter(
                    (option) =>
                      option.accountName.toLowerCase().includes(searchValue) ||
                      option.group.toLowerCase().includes(searchValue)
                  );
                }}
                ListboxProps={{
                  onWheel: (e: React.WheelEvent) => {
                    const listbox = e.currentTarget;
                    if (listbox.scrollHeight <= listbox.clientHeight) return;
                    e.stopPropagation();
                    e.preventDefault();
                    listbox.scrollTop += e.deltaY;
                  },
                }}
                renderOption={(props, option: Account) => (
                  <Box component="li" {...props} key={option.id} sx={{ pl: 3 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                      <Typography variant="body2">
                        {option.accountName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                        {formatCurrency(option.amount || 0, option.currency, getCurrencyDecimalPlaces(option.currency, currencies, defaultCurrency))} ({option.currency})
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderGroup={(params) => (
                  <li key={params.key}>
                    <Box sx={{ position: "sticky", top: 0, bgcolor: "background.paper", zIndex: 1, py: 0.5, px: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                        {params.group}
                      </Typography>
                    </Box>
                    <Box component="ul" sx={{ p: 0 }}>
                      {params.children}
                    </Box>
                  </li>
                )}
                renderInput={(params) => {
                  const toAccount = transferFormData.toAccountId ? accounts.find((acc) => acc.id === transferFormData.toAccountId && !acc.isArchived) : null;
                  const toAccountCurrency = toAccount?.currency || defaultCurrency;
                  const fromAccount = transferFormData.fromAccountId ? accounts.find((acc) => acc.id === transferFormData.fromAccountId && !acc.isArchived) : null;
                  const fromAccountCurrency = fromAccount?.currency || defaultCurrency;
                  const transferAmount = parseFloat(transferFormData.amount || "0");
                  
                  const balanceHelperText = toAccount && transferFormData.amount && !isNaN(transferAmount) && transferAmount > 0
                    ? (() => {
                        let amountInToAccountCurrency = transferAmount;
                        
                        // If currencies differ, use transfer rate to convert
                        if (fromAccount && toAccount && fromAccountCurrency !== toAccountCurrency && transferFormData.transferRate) {
                          const rate = parseFloat(transferFormData.transferRate);
                          if (!isNaN(rate) && rate > 0) {
                            amountInToAccountCurrency = transferAmount * rate;
                          }
                        }
                        
                        // Calculate account balance before transfer (if editing)
                        let balanceBeforeTransfer = toAccount.amount;
                        if (editingTransaction && editingTransaction.type === "transfer" && editingTransaction.toAccountId === toAccount.id) {
                          // Reverse the old transfer: subtract the old converted amount
                          const oldAmount = editingTransaction.amount || 0;
                          let oldAmountInToAccountCurrency = oldAmount;
                          
                          // Handle currency conversion for old transfer
                          if (editingTransaction.transferRate) {
                            const oldRate = parseFloat(String(editingTransaction.transferRate));
                            if (!isNaN(oldRate) && oldRate > 0) {
                              oldAmountInToAccountCurrency = oldAmount * oldRate;
                            }
                          }
                          
                          balanceBeforeTransfer = toAccount.amount - oldAmountInToAccountCurrency;
                        }
                        
                        let newBalance = balanceBeforeTransfer + amountInToAccountCurrency;
                        
                        // Check if fee account matches To account and subtract fee
                        let feeAmount: number | undefined;
                        if (transferFormData.feeAmount && transferFormData.feeAccountId && transferFormData.feeAccountId === toAccount.id) {
                          feeAmount = parseFloat(transferFormData.feeAmount);
                          if (!isNaN(feeAmount) && feeAmount > 0) {
                            newBalance = newBalance - feeAmount;
                          }
                        }
                        
                        const isNegative = newBalance < 0;
                        const balanceText = `Balance after transfer: ${formatCurrency(newBalance, toAccountCurrency)}`;
                        const feeText = feeAmount ? ` (includes ${formatCurrency(feeAmount, toAccountCurrency)} fee)` : "";
                        const negativeText = isNegative ? " (negative)" : "";
                        return balanceText + feeText + negativeText;
                      })()
                    : undefined;
                  
                  const balanceHelperColor = toAccount && transferFormData.amount && !isNaN(transferAmount) && transferAmount > 0
                    ? (() => {
                        let amountInToAccountCurrency = transferAmount;
                        
                        // If currencies differ, use transfer rate to convert
                        if (fromAccount && toAccount && fromAccountCurrency !== toAccountCurrency && transferFormData.transferRate) {
                          const rate = parseFloat(transferFormData.transferRate);
                          if (!isNaN(rate) && rate > 0) {
                            amountInToAccountCurrency = transferAmount * rate;
                          }
                        }
                        
                        // Calculate account balance before transfer (if editing)
                        let balanceBeforeTransfer = toAccount.amount;
                        if (editingTransaction && editingTransaction.type === "transfer" && editingTransaction.toAccountId === toAccount.id) {
                          // Reverse the old transfer: subtract the old converted amount
                          const oldAmount = editingTransaction.amount || 0;
                          let oldAmountInToAccountCurrency = oldAmount;
                          
                          // Handle currency conversion for old transfer
                          if (editingTransaction.transferRate) {
                            const oldRate = parseFloat(String(editingTransaction.transferRate));
                            if (!isNaN(oldRate) && oldRate > 0) {
                              oldAmountInToAccountCurrency = oldAmount * oldRate;
                            }
                          }
                          
                          balanceBeforeTransfer = toAccount.amount - oldAmountInToAccountCurrency;
                        }
                        
                        let newBalance = balanceBeforeTransfer + amountInToAccountCurrency;
                        
                        // Check if fee account matches To account and subtract fee
                        if (transferFormData.feeAmount && transferFormData.feeAccountId && transferFormData.feeAccountId === toAccount.id) {
                          const feeAmount = parseFloat(transferFormData.feeAmount);
                          if (!isNaN(feeAmount) && feeAmount > 0) {
                            newBalance = newBalance - feeAmount;
                          }
                        }
                        
                        return newBalance < 0 ? "warning.main" : "text.secondary";
                      })()
                    : "text.secondary";
                  
                  return (
                    <TextField
                      {...params}
                      label="To Account"
                      required
                      helperText={balanceHelperText}
                      FormHelperTextProps={{
                        sx: { color: balanceHelperColor },
                      }}
                    />
                  );
                }}
                isOptionEqualToValue={(option: Account, value: Account) => option.id === value.id}
              />

              {/* Transfer Fee - same pattern as expense fee (only when transfer fee is enabled) */}
              {allowTransferFee && (
              <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                {(() => {
                  const isEditingWithFee = !!(editingTransaction && editingTransaction.type === "transfer" && transferFormData.feeAmount !== undefined && transferFormData.feeAmount !== "");
                  const button = (
                    <Button
                      type="button"
                      size="small"
                      variant={transferFormData.feeAmount !== undefined ? "outlined" : "text"}
                      onClick={() => {
                        if (transferFormData.feeAmount === undefined) {
                          setShowTransferFee(true);
                          setTransferFormData({ ...transferFormData, feeAmount: "", feeAccountId: "" });
                        } else {
                          setTransferFormData({ ...transferFormData, feeAmount: undefined, feeAccountId: undefined });
                          setShowTransferFee(false);
                        }
                      }}
                      disabled={isEditingWithFee}
                      sx={{
                        minWidth: "auto",
                        px: 1,
                        py: 0.25,
                        textTransform: "none",
                        "&:hover": { bgcolor: "action.hover" },
                        "&.Mui-disabled": { color: "text.disabled" },
                      }}
                    >
                      {transferFormData.feeAmount === undefined ? "+ Add fee" : "Remove fee"}
                    </Button>
                  );
                  return isEditingWithFee ? (
                    <Tooltip title="Can't delete fee. Change fee value to 0 instead.">
                      <span>{button}</span>
                    </Tooltip>
                  ) : button;
                })()}
                <Tooltip title="Optional fee (e.g. bank fee):" placement="top">
                  <IconButton size="small" sx={{ p: 0.25, color: "text.disabled", "&:hover": { color: "text.secondary" } }} aria-label="Optional fee info">
                    <InfoIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              )}

              {/* Exchange Rate Field - Show only when currencies differ */}
              {transferFormData.fromAccountId &&
                transferFormData.toAccountId &&
                accounts.find((acc) => acc.id === transferFormData.fromAccountId)?.currency !==
                  accounts.find((acc) => acc.id === transferFormData.toAccountId)?.currency && (() => {
                    const fromAccount = accounts.find((acc) => acc.id === transferFormData.fromAccountId && !acc.isArchived);
                    const toAccount = accounts.find((acc) => acc.id === transferFormData.toAccountId && !acc.isArchived);
                    const calculatedRate = fromAccount && toAccount ? calculateTransferRate(fromAccount.currency, toAccount.currency) : null;
                    const currentRate = transferFormData.transferRate ? parseFloat(transferFormData.transferRate) : null;
                    const rateMatchesSetting = calculatedRate !== null && currentRate !== null && !isNaN(currentRate) && Math.abs(currentRate - calculatedRate) < 0.000001;
                    const shouldDisableRefresh = rateMatchesSetting || !transferRateManuallyEdited;
                    
                    // Calculate converted amount
                    const transferAmount = transferFormData.amount ? parseFloat(transferFormData.amount) : null;
                    const convertedAmount = transferAmount && currentRate && !isNaN(currentRate) && currentRate > 0
                      ? transferAmount * currentRate
                      : null;
                    const toAccountDecimalPlaces = toAccount ? getCurrencyDecimalPlaces(toAccount.currency, currencies, defaultCurrency) : getCurrencyDecimalPlaces(defaultCurrency, currencies, defaultCurrency);
                    const fromAccountDecimalPlaces = fromAccount ? getCurrencyDecimalPlaces(fromAccount.currency, currencies, defaultCurrency) : getCurrencyDecimalPlaces(defaultCurrency, currencies, defaultCurrency);
                    
                    return (
                      <TextField
                        required
                        fullWidth
                        label="Exchange Rate"
                        type="number"
                        inputProps={{ step: "0.000001", min: "0.000001" }}
                        value={transferFormData.transferRate}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Allow digits and decimal point, reject scientific notation and negative signs
                          if (value === "" || /^\d*\.?\d*$/.test(value)) {
                            setTransferFormData({ ...transferFormData, transferRate: value });
                            setTransferRateManuallyEdited(true);
                          }
                        }}
                        onPaste={(e) => {
                          e.preventDefault();
                          const pastedText = e.clipboardData.getData('text');
                          // Sanitize pasted content: remove all non-digit and non-decimal characters
                          const sanitized = pastedText.replace(/[^\d.]/g, '');
                          // Remove multiple decimal points, keeping only the first one
                          const parts = sanitized.split('.');
                          const cleaned = parts.length > 0 
                            ? parts[0] + (parts.length > 1 ? '.' + parts.slice(1).join('') : '')
                            : '';
                          // Only allow valid number format
                          if (cleaned === "" || /^\d*\.?\d*$/.test(cleaned)) {
                            setTransferFormData({ ...transferFormData, transferRate: cleaned });
                            setTransferRateManuallyEdited(true);
                          }
                        }}
                        placeholder="Auto-filled from currency settings"
                        helperText={(() => {
                          if (!fromAccount || !toAccount) return "";
                          
                          // Calculate default currency rate for from currency
                          const fromDefaultRate = fromAccount.currency !== defaultCurrency 
                            ? calculateTransferRate(defaultCurrency, fromAccount.currency)
                            : 1;
                          const fromDefaultRateDisplay = fromAccount.currency !== defaultCurrency 
                            ? formatRate(fromDefaultRate)
                            : "1";
                          
                          // Calculate converted amount
                          const convertedAmountDisplay = convertedAmount 
                            ? formatCurrency(convertedAmount, toAccount.currency, toAccountDecimalPlaces)
                            : "";
                          
                          // Build helper text: "1 USD = 375.9398 AMD | = AMD 3,759.4"
                          const defaultRateText = `1 ${defaultCurrency} = ${fromDefaultRateDisplay} ${fromAccount.currency}`;
                          const convertedAmountText = convertedAmountDisplay ? ` | = ${convertedAmountDisplay}` : "";
                          return defaultRateText + convertedAmountText;
                        })()}
                        InputProps={{
                          endAdornment: (
                            <RefreshIconButton
                              onClick={handleRefreshTransferRate}
                              disabled={shouldDisableRefresh}
                              disabledTooltip={rateMatchesSetting || !transferRateManuallyEdited ? "Rate matches the setting rate." : "Rate has been manually edited. Clear the field to enable refresh."}
                              tooltip="Refresh rate from currency settings"
                            />
                          ),
                          sx: {
                            "& input[type=number]": {
                              MozAppearance: "textfield",
                            },
                            "& input[type=number]::-webkit-outer-spin-button": {
                              WebkitAppearance: "none",
                              margin: 0,
                            },
                            "& input[type=number]::-webkit-inner-spin-button": {
                              WebkitAppearance: "none",
                              margin: 0,
                            },
                          },
                        }}
                      />
                    );
                  })()}

              {/* Live Calculation Display */}
              {getConvertedAmount() !== null && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: "action.hover",
                    color: "text.primary",
                    borderRadius: 1,
                    border: 1,
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="body2" gutterBottom fontWeight="medium" color="text.primary">
                    Transfer Calculation:
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
                    <Typography variant="body1" color="text.primary">
                      {formatCurrency(
                        parseFloat(transferFormData.amount),
                        accounts.find((acc) => acc.id === transferFormData.fromAccountId && !acc.isArchived)?.currency || "USD"
                      )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">→</Typography>
                    <Typography variant="body1" fontWeight="bold" color="text.primary">
                      {formatCurrency(
                        getConvertedAmount() || 0,
                        accounts.find((acc) => acc.id === transferFormData.toAccountId && !acc.isArchived)?.currency || "USD"
                      )}
                    </Typography>
                  </Stack>
                  {transferFormData.transferRate && (
                    <Typography variant="caption" sx={{ mt: 0.5, display: "block" }} color="text.secondary">
                      Rate: {formatRate(transferFormData.transferRate)}
                    </Typography>
                  )}
                </Paper>
              )}

              {/* Custom fields for transfer (only for transfer type) */}
              {transferFieldsForType.length > 0 && (
                <Box sx={{ pt: 1 }}>
                  <Stack spacing={1.5}>
                    {transferFieldsForType.map((field) => {
                      const value = transferFormData.customFieldValues?.[field.id];
                      const pendingFile = transferFormData.customFieldPendingFiles?.[field.id];
                      const attachmentValue = value && typeof value === "object" && "url" in value ? (value as CustomFieldAttachmentValue) : null;
                      const displayName = pendingFile?.name ?? attachmentValue?.name ?? "";
                      if (field.type === "text") {
                        return (
                          <TextField
                            key={field.id}
                            label={
                              <Stack direction="row" alignItems="center" gap={0.5}>
                                <TransactionFieldIcon icon={field.icon} />
                                <span>{field.label}</span>
                              </Stack>
                            }
                            fullWidth
                            value={typeof value === "string" ? value : ""}
                            onChange={(e) => {
                              setTransferFormData((prev) => ({
                                ...prev,
                                customFieldValues: {
                                  ...(prev.customFieldValues || {}),
                                  [field.id]: e.target.value,
                                },
                              }));
                            }}
                            disabled={isSubmittingTransfer}
                          />
                        );
                      }
                      return (
                        <TextField
                          key={field.id}
                          fullWidth
                          label={
                            <Stack direction="row" alignItems="center" gap={0.5}>
                              <TransactionFieldIcon icon={field.icon} />
                              <span>{field.label}</span>
                            </Stack>
                          }
                          value={displayName}
                          placeholder={`${field.label} (PDF, PNG, JPG…) · Max 10MB`}
                          disabled={isSubmittingTransfer}
                          onClick={() => {
                            if (!isSubmittingTransfer) {
                              customFieldFileTargetRef.current = field.id;
                              transferCustomFieldFileInputRef.current?.click();
                            }
                          }}
                          InputProps={{
                            readOnly: true,
                            startAdornment: displayName ? (
                              <InputAdornment position="start" sx={{ mr: 0 }}>
                                <Tooltip title={attachmentValue?.url ? "Preview" : displayName}>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (attachmentValue?.url) openAttachment(attachmentValue.url);
                                    }}
                                    sx={{ p: 0.5 }}
                                    color="primary"
                                    disabled={!attachmentValue?.url}
                                  >
                                    <AttachmentIcon
                                      attachmentType={attachmentValue?.type ?? pendingFile?.type}
                                      attachmentName={displayName}
                                    />
                                  </IconButton>
                                </Tooltip>
                              </InputAdornment>
                            ) : null,
                            endAdornment: (
                              <InputAdornment position="end">
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                  {displayName ? (
                                    <>
                                      {attachmentValue?.url && (
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openAttachment(attachmentValue!.url);
                                          }}
                                          color="primary"
                                        >
                                          <DownloadIcon fontSize="small" />
                                        </IconButton>
                                      )}
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          customFieldFileTargetRef.current = field.id;
                                          transferCustomFieldFileInputRef.current?.click();
                                        }}
                                        disabled={isSubmittingTransfer}
                                      >
                                        <AttachFileIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setTransferFormData((prev) => {
                                            const next = { ...prev };
                                            if (next.customFieldPendingFiles?.[field.id]) {
                                              const { [field.id]: _, ...rest } = next.customFieldPendingFiles;
                                              next.customFieldPendingFiles = Object.keys(rest).length ? rest : undefined;
                                            }
                                            if (next.customFieldValues?.[field.id]) {
                                              const cv = { ...(next.customFieldValues || {}) };
                                              delete cv[field.id];
                                              next.customFieldValues = Object.keys(cv).length ? cv : undefined;
                                            }
                                            return next;
                                          });
                                        }}
                                        disabled={isSubmittingTransfer}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </>
                                  ) : (
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        customFieldFileTargetRef.current = field.id;
                                        transferCustomFieldFileInputRef.current?.click();
                                      }}
                                      disabled={isSubmittingTransfer}
                                      color="primary"
                                    >
                                      <AttachFileIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                </Box>
                              </InputAdornment>
                            ),
                          }}
                          helperText={undefined}
                          sx={{ cursor: isSubmittingTransfer ? "default" : "pointer" }}
                        />
                      );
                    })}
                  </Stack>
                  <input
                    type="file"
                    ref={transferCustomFieldFileInputRef}
                    style={{ display: "none" }}
                    accept="image/*,.pdf"
                    onChange={(e) => {
                      const fieldId = customFieldFileTargetRef.current;
                      const file = e.target.files?.[0];
                      if (fieldId && file) {
                        setTransferFormData((prev) => ({
                          ...prev,
                          customFieldPendingFiles: {
                            ...(prev.customFieldPendingFiles || {}),
                            [fieldId]: file,
                          },
                        }));
                      }
                      e.target.value = "";
                      customFieldFileTargetRef.current = null;
                    }}
                  />
                </Box>
              )}
            </Stack>
          </form>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, pt: 1, gap: 1, display: "flex" }}>
          {viewOnly ? (
            <>
              <Button onClick={handleTransferDialogClose} variant="outlined" sx={{ flex: 1, py: 1.5 }}>
                Close
              </Button>
              <Button variant="contained" startIcon={<EditIcon />} onClick={onSwitchToEdit} sx={{ flex: 1, py: 1.5 }}>
                Edit transaction
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleTransferDialogClose} variant="outlined" sx={{ flex: 1, py: 1.5 }}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                form="transfer-form" 
                variant="contained"
                sx={{ flex: 1, py: 1.5 }}
                disabled={isSubmittingTransfer || (editingTransaction && editingTransaction.type === "transfer" ? !hasTransferChanges() : !isTransferFormValid)}
              >
                {editingTransaction && editingTransaction.type === "transfer" ? "Update Transfer" : "Transfer"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TransactionDialog;
